import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useStore } from "@/lib/store";
import { apiRequest, apiRequestSSE, uploadFile, queryClient } from "@/lib/queryClient";
import { invalidateOfferSheetCaches } from "@/lib/offer-sheet-cache";
import { sanitizeErrorMessage } from "@/lib/errors/gateway";
import { FriendlyErrorMapper } from "@/lib/errors/FriendlyErrorMapper";
import { useAuth } from "@/hooks/useAuth";
import { canAccessGoogleAdsIntegration } from "@shared/google-ads-access";
import { useAgencyClients } from "@/hooks/useAgencyClients";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { VoiceButton } from "./ui/voice-button";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { useAnalytics } from "@/hooks/useAnalytics";
import { motion, AnimatePresence } from "framer-motion";
import ReactMarkdown from "react-markdown";
import { Conversation, Message, OfferSheet } from "@shared/schema";
import { OfferSaveDialog } from "./OfferSaveDialog";
import { PaywallModal } from "./paywall-modal";
import { DemoBookingModal } from "./demo-booking-modal";
import AdGraphicsBuilder from "./ad-graphics-builder";
import GraphicsLibrary from "./graphics-library";
import AdVideoBuilder from "./ad-video-builder";
import AdTypeSelector, { type AdType, buildAdTypes } from "./ad-type-selector";
import MetaConnectPanel from "./meta-connect-panel";
import GhlConnectPanel from "./ghl-connect-panel";
import GoogleAdsConnectPanel from "./google-ads-connect-panel";
import AllegroConnectPanel from "./allegro-connect-panel";
import GmcConnectPanel from "./gmc-connect-panel";
import BaseLinkerConnectPanel from "./baselinker-connect-panel";
import ShopifyConnectPanel from "./shopify-connect-panel";
import TrackingConnectPanel from "./tracking-connect-panel";
import RecommendationsPanel from "./recommendations-panel";
import AdSetActionsPanel from "./adset-actions-panel";
import LeadFormSwapPanel from "./lead-form-swap-panel";
import { parseCarouselResponse } from "./carousel-creator/carousel-parser";
import {
  parseStoriesScript,
  looksLikeStoriesScriptAttempt,
} from "./stories-creator/stories-parser";
import StoriesControlsBar from "./stories-creator/stories-controls-bar";
import StoryFramesPreview from "./stories-creator/story-frames-preview";
import StoriesPresetGallery from "./stories-creator/stories-preset-gallery";
import StoriesShowcaseMarquee from "./stories-creator/stories-showcase-marquee";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import {
  Image,
  Paperclip,
  Send,
  Package,
  Video,
  Megaphone,
  BarChart3,
  Film,
  Lightbulb,
  Youtube,
  Users,
  Mail,
  Lock,
  Play,
  ExternalLink,
  Check,
  UserPlus,
  Brain,
  PlayCircle,
  Upload,
  ChevronDown,
  ChevronUp,
  Plug,
  Rocket,
  AlertTriangle,
  CheckCircle,
  Loader2,
  Copy as CopyIcon,
  Link as LinkIcon,
  ClipboardList,
  MessageCircle,
  X as XIcon,
  PanelLeftOpen,
  Handshake,
  FileText,
  LayoutGrid,
  Type,
  Palette,
  Library,
  Clapperboard,
  Workflow,
  Globe,
  ShoppingBag,
  GraduationCap,
  Laugh,
  TrendingUp,
  Timer,
  ArrowLeft,
  RotateCw,
  type LucideIcon,
} from "lucide-react";
import { REEL_CATEGORY_IDS, type ReelCategoryId } from "@shared/reel-categories";
import { normalizeWebsiteUrl } from "@shared/website-url";
import { shouldForwardWheel, wheelDeltaToPixels } from "@/lib/wheel-forwarding";
import { getModuleIcon } from "@/utils/module-icons";
import AnalystDashboard from "./analyst-dashboard";
import { ManualEntityControls } from "./manual-entity-controls";
import CreativePipeline from "./creative-pipeline";
import { UgcStudioBuilder } from "./ugc-studio/ugc-studio-builder";
import { useRotatingMessage } from "./ui/ai-generating-overlay";
import { useTheme } from "./theme-provider";

// ─── Chat Action types & helpers (for marketing-analyst chat mode) ───
type ChatAction = {
  type:
    | "duplicate_adset"
    | "duplicate_adset_with_ads"
    | "update_ad_link"
    | "swap_lead_form"
    | "update_budget"
    | "update_status";
  adSetId?: string;
  entityId?: string;
  entityType?: string;
  entityName?: string;
  name?: string;
  status?: string;
  newUrl?: string;
  newFormId?: string;
  scope?: "ad" | "adset" | "campaign";
  newDailyBudget?: number;
  newStatus?: string;
  description?: string;
};

function extractChatActions(content: string): { cleanContent: string; actions: ChatAction[] } {
  const actions: ChatAction[] = [];
  const cleanContent = content
    .replace(/<!--\s*ACTION:(.*?)-->/gs, (_, jsonStr) => {
      try {
        const parsed = JSON.parse(jsonStr.trim());
        if (parsed.type) actions.push(parsed as ChatAction);
      } catch {}
      return "";
    })
    .trim();
  return { cleanContent, actions };
}

// ─── Campaign Creator interactive options parser ───
type CampaignOption = { id: string; label: string };

const OPEN_OPTION_KEYWORDS = [
  "opisz",
  "opiszę",
  "innego",
  "inne",
  "własn",
  "wpisz",
  "describe",
  "other",
];

function isOpenOption(option: CampaignOption): boolean {
  const text = `${option.id} ${option.label}`.toLowerCase();
  return OPEN_OPTION_KEYWORDS.some((keyword) => text.includes(keyword));
}

function extractCampaignOptions(content: string): {
  cleanContent: string;
  options: CampaignOption[];
} {
  const options: CampaignOption[] = [];
  const cleanContent = content
    .replace(/\[OPTIONS\]\s*([\s\S]*?)\s*\[\/OPTIONS\]/g, (_, block: string) => {
      block
        .trim()
        .split("\n")
        .forEach((line) => {
          const trimmed = line.trim();
          if (!trimmed) return;
          const pipeIndex = trimmed.indexOf("|");
          if (pipeIndex > 0) {
            options.push({
              id: trimmed.slice(0, pipeIndex).trim(),
              label: trimmed.slice(pipeIndex + 1).trim(),
            });
          } else {
            options.push({ id: trimmed, label: trimmed });
          }
        });
      return "";
    })
    .trim();
  return { cleanContent, options };
}

interface CampaignSaveBlock {
  type: "offer" | "kpi";
  content: string;
}

function extractCampaignSaveBlocks(content: string): {
  cleanContent: string;
  saveBlocks: CampaignSaveBlock[];
} {
  const saveBlocks: CampaignSaveBlock[] = [];
  let cleanContent = content.replace(
    /\[SAVE_OFFER\]([\s\S]*?)\[\/SAVE_OFFER\]/g,
    (_, block: string) => {
      saveBlocks.push({ type: "offer", content: block.trim() });
      return "";
    }
  );
  cleanContent = cleanContent.replace(
    /\[SAVE_KPI\]([\s\S]*?)\[\/SAVE_KPI\]/g,
    (_, block: string) => {
      saveBlocks.push({ type: "kpi", content: block.trim() });
      return "";
    }
  );
  return { cleanContent: cleanContent.trim(), saveBlocks };
}

const PROGRESS_STEPS: Record<string, { steps: string[]; intervalMs: number }> = {
  duplicate_adset_with_ads: {
    steps: [
      "progressSteps.readingAdSetConfig",
      "progressSteps.creatingNewAdSet",
      "progressSteps.fetchingSourceAds",
      "progressSteps.copyingAd1",
      "progressSteps.creatingCreative",
      "progressSteps.copyingAd2",
      "progressSteps.creatingCreative",
      "progressSteps.copyingAd3",
      "progressSteps.updatingLinks",
      "progressSteps.finalizingDuplicate",
    ],
    intervalMs: 3000,
  },
  duplicate_adset: {
    steps: [
      "progressSteps.readingConfig",
      "progressSteps.creatingNewAdSet",
      "progressSteps.finalization",
    ],
    intervalMs: 2000,
  },
  update_ad_link: {
    steps: [
      "progressSteps.readingCreative",
      "progressSteps.creatingNewCreativeWithLink",
      "progressSteps.updatingAd",
    ],
    intervalMs: 2000,
  },
  swap_lead_form: {
    steps: [
      "progressSteps.collectingAds",
      "progressSteps.readingCreative",
      "progressSteps.creatingNewCreativeWithForm",
      "progressSteps.updatingAd",
    ],
    intervalMs: 3000,
  },
  update_budget: {
    steps: ["progressSteps.updatingBudget"],
    intervalMs: 1500,
  },
  update_status: {
    steps: ["progressSteps.changingStatus"],
    intervalMs: 1500,
  },
};

function ChatActionButton({ action, forUserId }: { action: ChatAction; forUserId?: string }) {
  const { t } = useTranslation("chatInterface");
  const [confirmed, setConfirmed] = React.useState(false);
  const [applied, setApplied] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [result, setResult] = React.useState<string | null>(null);
  const [metaUrl, setMetaUrl] = React.useState<string | null>(null);
  const [progressStep, setProgressStep] = React.useState(0);
  const [completedSteps, setCompletedSteps] = React.useState<string[]>([]);
  const progressRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

  const execute = async () => {
    setLoading(true);
    setError(null);
    setProgressStep(0);
    setCompletedSteps([]);

    const config = PROGRESS_STEPS[action.type];
    if (config) {
      let step = 0;
      progressRef.current = setInterval(() => {
        step++;
        if (step < config.steps.length) {
          setCompletedSteps((prev) => [...prev, config.steps[step - 1]]);
          setProgressStep(step);
        }
      }, config.intervalMs);
    }

    try {
      const resp = await apiRequest<{ message?: string; metaAdsManagerUrl?: string }>(
        "POST",
        "/api/marketing-analyst/chat-action",
        {
          ...action,
          forUserId,
        }
      );
      setApplied(true);
      setConfirmed(false);
      setResult(resp.message || t("chatActionButton.executed"));
      if (resp.metaAdsManagerUrl) setMetaUrl(resp.metaAdsManagerUrl);
    } catch (err: unknown) {
      setError(FriendlyErrorMapper.map(err).description);
    } finally {
      if (progressRef.current) clearInterval(progressRef.current);
      setLoading(false);
    }
  };

  if (applied) {
    return (
      <div className="space-y-1.5 mt-2">
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-950/40 border border-emerald-700/30 text-emerald-300 text-xs">
          <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="flex-1">{result}</span>
        </div>
        {metaUrl && (
          <a
            href={metaUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-950/40 border border-blue-600/30 text-blue-300 hover:bg-blue-950/60 hover:border-blue-500/40 text-xs font-medium transition-all"
          >
            <ExternalLink className="w-3 h-3" />
            {t("chatActionButton.checkInMeta")}
          </a>
        )}
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-1.5 mt-2">
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-950/40 border border-red-700/30 text-red-300 text-xs">
          <AlertTriangle className="w-3.5 h-3.5" />
          <span>{error}</span>
        </div>
        <button
          onClick={() => {
            setError(null);
            setConfirmed(false);
          }}
          className="text-xs text-white/50 hover:text-white/70"
        >
          {t("chatActionButton.tryAgain")}
        </button>
      </div>
    );
  }

  const actionLabels: Record<string, { icon: React.ReactNode; label: string; color: string }> = {
    duplicate_adset: {
      icon: <CopyIcon className="w-3.5 h-3.5" />,
      label: t("chatActionButton.duplicateAdSet", { name: action.name || action.adSetId }),
      color:
        "border-blue-400/50 bg-blue-50 text-blue-700 hover:bg-blue-100 dark:border-blue-600/40 dark:bg-blue-950/30 dark:text-blue-300 dark:hover:bg-blue-950/50",
    },
    duplicate_adset_with_ads: {
      icon: <CopyIcon className="w-3.5 h-3.5" />,
      label: t("chatActionButton.duplicateAdSetWithAds", {
        name: action.name || action.adSetId,
        suffix: action.newUrl ? ` → ${action.newUrl.slice(0, 35)}...` : "",
      }),
      color:
        "border-purple-400/50 bg-purple-50 text-purple-700 hover:bg-purple-100 dark:border-purple-600/40 dark:bg-purple-950/30 dark:text-purple-300 dark:hover:bg-purple-950/50",
    },
    update_ad_link: {
      icon: <LinkIcon className="w-3.5 h-3.5" />,
      label: t("chatActionButton.changeLink", { url: action.newUrl?.slice(0, 40) }),
      color:
        "border-cyan-400/50 bg-cyan-50 text-cyan-700 hover:bg-cyan-100 dark:border-cyan-600/40 dark:bg-cyan-950/30 dark:text-cyan-300 dark:hover:bg-cyan-950/50",
    },
    swap_lead_form: {
      icon: <ClipboardList className="w-3.5 h-3.5" />,
      label: t("chatActionButton.swapLeadForm", {
        scope: t(`chatActionButton.swapScope.${action.scope || "adset"}`),
        name: action.entityName || action.entityId,
      }),
      color:
        "border-emerald-400/50 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-600/40 dark:bg-emerald-950/30 dark:text-emerald-300 dark:hover:bg-emerald-950/50",
    },
    update_budget: {
      icon: <BarChart3 className="w-3.5 h-3.5" />,
      label: action.description || t("chatActionButton.changeBudget", { name: action.entityName }),
      color:
        "border-amber-400/50 bg-amber-50 text-amber-700 hover:bg-amber-100 dark:border-amber-600/40 dark:bg-amber-950/30 dark:text-amber-300 dark:hover:bg-amber-950/50",
    },
    update_status: {
      icon:
        action.newStatus === "PAUSED" ? (
          <Play className="w-3.5 h-3.5" />
        ) : (
          <Play className="w-3.5 h-3.5" />
        ),
      label: action.description || `${action.newStatus}: ${action.entityName}`,
      color:
        "border-orange-400/50 bg-orange-50 text-orange-700 hover:bg-orange-100 dark:border-orange-600/40 dark:bg-orange-950/30 dark:text-orange-300 dark:hover:bg-orange-950/50",
    },
  };

  const meta = actionLabels[action.type] || {
    icon: <Play className="w-3.5 h-3.5" />,
    label: action.type,
    color: "border-white/20 bg-white/5 text-white/80",
  };

  if (!confirmed) {
    return (
      <button
        onClick={() => setConfirmed(true)}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-all mt-2 ${meta.color}`}
      >
        {meta.icon}
        <span>{t("chatActionButton.execute", { label: meta.label })}</span>
      </button>
    );
  }

  if (loading) {
    const config = PROGRESS_STEPS[action.type];
    const currentStepLabel = config?.steps[progressStep] || "chatActionButton.processing";
    return (
      <div className="space-y-2 mt-2 px-3 py-3 rounded-lg border border-purple-600/30 bg-purple-950/20">
        <div className="flex items-center gap-2 text-xs text-purple-300 font-medium">
          <Loader2 className="w-3.5 h-3.5 animate-spin flex-shrink-0" />
          <span>{t(currentStepLabel)}</span>
        </div>
        {completedSteps.length > 0 && (
          <div className="space-y-1 ml-5">
            {completedSteps.map((step, i) => (
              <div key={i} className="flex items-center gap-1.5 text-xs text-emerald-400/70">
                <CheckCircle className="w-3 h-3 flex-shrink-0" />
                <span>{t(step)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-amber-600/40 bg-amber-950/30 mt-2">
      <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
      <span className="text-xs text-amber-200 flex-1">
        {t("chatActionButton.confirm", { label: meta.label })}
      </span>
      <button
        onClick={execute}
        disabled={loading}
        className="flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium bg-emerald-600/30 text-emerald-300 hover:bg-emerald-600/50 border border-emerald-500/40 disabled:opacity-50"
      >
        <CheckCircle className="w-3 h-3" />
        {t("chatActionButton.yes")}
      </button>
      <button
        onClick={() => setConfirmed(false)}
        className="px-2.5 py-1 rounded text-xs text-white/60 hover:text-white/80"
      >
        {t("chatActionButton.no")}
      </button>
    </div>
  );
}

// ─── Campaign Creator KPI Save Button (with per-button saved state) ───
function CampaignKpiSaveButton({
  block,
  clientId,
  clientName,
}: {
  block: CampaignSaveBlock;
  clientId: string | null;
  clientName?: string;
}) {
  const { t } = useTranslation("chatInterface");
  const { toast } = useToast();
  const [saved, setSaved] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);

  const handleSave = async () => {
    setSaving(true);
    setErr(null);
    try {
      const title = `KPI/Budżet — ${clientName || new Date().toLocaleDateString("pl-PL")}`;
      if (clientId) {
        await apiRequest("PUT", `/api/my-clients/${clientId}/offer-sheet`, {
          title,
          content: block.content,
        });
      } else {
        await apiRequest("POST", "/api/offer-sheets", { title, content: block.content });
      }
      // VP-94: świeżo zapisany arkusz musi pojawić się we wszystkich pickerach bez reloadu.
      invalidateOfferSheetCaches(queryClient, clientId);
      setSaved(true);
      toast({
        title: t("campaignKpiSave.savedKpi"),
        description: clientId
          ? t("campaignKpiSave.savedToClient", { name: clientName })
          : t("campaignKpiSave.savedToOfferSheets"),
      });
    } catch (e) {
      const msg = (e as Error)?.message ? sanitizeErrorMessage(e) : t("campaignKpiSave.tryAgain");
      setErr(msg);
      toast({ title: t("campaignKpiSave.tryAgain"), description: msg, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  if (saved) {
    return (
      <div className="mt-3 flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm bg-emerald-950/40 border border-emerald-700/30 text-emerald-300">
        <CheckCircle className="w-4 h-4 flex-shrink-0" />
        <span>
          {clientName
            ? t("campaignKpiSave.kpiSavedForClient", { name: clientName })
            : t("campaignKpiSave.kpiSaved")}
        </span>
      </div>
    );
  }

  return (
    <div className="mt-3">
      <button
        onClick={handleSave}
        disabled={saving}
        className="px-4 py-2.5 rounded-xl text-sm font-medium bg-green-600/20 hover:bg-green-600/40 border border-green-500/30 hover:border-green-400/60 text-green-300 hover:text-green-100 transition-all flex items-center gap-2 disabled:opacity-50"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <span>💾</span>}
        {saving
          ? t("campaignKpiSave.saving")
          : clientId
            ? t("campaignKpiSave.saveKpiToClient", { name: clientName || "" })
            : t("campaignKpiSave.saveKpi")}
      </button>
      {err && <div className="mt-1 text-xs text-red-400">{err}</div>}
    </div>
  );
}

// ─── Campaign Creator: Step 4 — Creative Assets Panel ───
const CREATIVE_MODELS = [
  { id: "realistic", label: "Realistyczny" },
  { id: "modern", label: "Nowoczesny" },
  { id: "bold", label: "Odważny" },
  { id: "minimal", label: "Minimalistyczny" },
  { id: "premium", label: "Premium/Luksusowy" },
];

const CREATIVE_LAYOUTS = [
  { id: "1:1", label: "1:1 Feed" },
  { id: "4:5", label: "4:5 Feed" },
  { id: "9:16", label: "9:16 Story/Reel" },
  { id: "16:9", label: "16:9 Poziomy" },
];

interface CreativeAssetsPanelProps {
  onAssetsChange: (urls: string[]) => void;
  onModelChange: (model: string) => void;
  onLayoutChange: (layout: string) => void;
  currentAssets: string[];
  currentModel: string;
  currentLayout: string;
}

function CampaignCreativeAssetsPanel({
  onAssetsChange,
  onModelChange,
  onLayoutChange,
  currentAssets,
  currentModel,
  currentLayout,
}: CreativeAssetsPanelProps) {
  const { t } = useTranslation("chatInterface");
  const { toast } = useToast();
  const fileRef = React.useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = React.useState(false);
  const [previewOpen, setPreviewOpen] = React.useState(false);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded: string[] = [];
      for (const file of files) {
        const form = new FormData();
        form.append("file", file);
        const resp = await fetch("/api/upload", { method: "POST", body: form });
        if (!resp.ok) throw new Error(t("creativeAssetsPanel.uploadError"));
        const data = await resp.json();
        uploaded.push(data.url || data.fileUrl || data.publicUrl);
      }
      onAssetsChange([...currentAssets, ...uploaded]);
      toast({
        title: t("creativeAssetsPanel.uploadedSuccess"),
        description: t("creativeAssetsPanel.filesUploaded", { count: uploaded.length }),
      });
    } catch (err) {
      toast({
        title: t("creativeAssetsPanel.retryTitle"),
        description: t("creativeAssetsPanel.retryDescription"),
        variant: "destructive",
      });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeAsset = (idx: number) => {
    onAssetsChange(currentAssets.filter((_, i) => i !== idx));
  };

  return (
    <div className="p-3 bg-orange-950/20 border border-orange-700/30 rounded-lg space-y-3">
      <div className="flex items-center gap-2">
        <Image className="w-4 h-4 text-orange-400" />
        <span className="text-xs font-semibold text-orange-300">
          {t("creativeAssetsPanel.title")}
        </span>
        <span className="ml-auto text-[10px] text-orange-400/70">
          {t("creativeAssetsPanel.step")}
        </span>
      </div>

      {/* Upload trigger */}
      <div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*,video/*,application/pdf"
          multiple
          className="hidden"
          onChange={handleFileChange}
        />
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border-2 border-dashed border-orange-600/40 hover:border-orange-500/70 bg-orange-950/10 hover:bg-orange-950/30 text-orange-300 text-xs font-medium transition-all disabled:opacity-50"
        >
          {uploading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Upload className="w-4 h-4" />
          )}
          {uploading ? t("creativeAssetsPanel.uploading") : t("creativeAssetsPanel.addMedia")}
        </button>
      </div>

      {/* Uploaded assets list */}
      {currentAssets.length > 0 && (
        <div className="space-y-1">
          {currentAssets.map((url, i) => {
            const name =
              url.split("/").pop()?.split("?")[0] ||
              `${t("creativeAssetsPanel.filePrefix")} ${i + 1}`;
            return (
              <div
                key={i}
                className="flex items-center gap-2 px-2 py-1.5 bg-white/[0.04] rounded border border-white/[0.08] text-xs"
              >
                <Image className="w-3 h-3 text-white/50 flex-shrink-0" />
                <span className="flex-1 truncate text-white/70">{name}</span>
                <button
                  onClick={() => removeAsset(i)}
                  className="text-white/40 hover:text-red-400 transition-colors"
                >
                  <XIcon className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Model + Layout selectors */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] text-white/50 mb-1 block">
            {t("creativeAssetsPanel.graphicStyle")}
          </label>
          <select
            value={currentModel}
            onChange={(e) => onModelChange(e.target.value)}
            className="w-full h-8 text-xs bg-[#030407] border border-white/[0.1] rounded text-white px-2"
          >
            {CREATIVE_MODELS.map((m) => (
              <option key={m.id} value={m.id}>
                {t(`creativeModels.${m.id}`)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[10px] text-white/50 mb-1 block">
            {t("creativeAssetsPanel.formatLayout")}
          </label>
          <select
            value={currentLayout}
            onChange={(e) => onLayoutChange(e.target.value)}
            className="w-full h-8 text-xs bg-[#030407] border border-white/[0.1] rounded text-white px-2"
          >
            {CREATIVE_LAYOUTS.map((l) => (
              <option key={l.id} value={l.id}>
                {l.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="text-[10px] text-orange-400/60">{t("creativeAssetsPanel.aiNote")}</div>
    </div>
  );
}

// ─── Module content length limits (must match server-side Zod schemas) ───
const MODULE_CONTENT_LIMITS: Record<string, number> = {
  "offer-builder": 8000,
  "ad-creator": 8000,
  "vsl-creator": 10000,
  "carousel-creator": 10000,
  "stories-creator": 8000,
  "reel-creator": 10000,
  "marketing-analyst": 12000,
  "campaign-creator": 12000,
  "content-creator": 12000,
  "youtube-mode": 12000,
  "email-creator": 12000,
};
const DEFAULT_CONTENT_LIMIT = 12000;

// VP-100: lucide icon per viral content category (id → component).
const REEL_CATEGORY_ICONS: Record<ReelCategoryId, LucideIcon> = {
  "product-promo": ShoppingBag,
  "educational-reel": GraduationCap,
  "entertaining-relatable": Laugh,
  "problem-solution": Lightbulb,
  "social-proof-case": TrendingUp,
  "behind-the-scenes": Clapperboard,
  "limited-time-offer": Timer,
};

function getContentLimit(moduleId: string): number {
  return MODULE_CONTENT_LIMITS[moduleId] ?? DEFAULT_CONTENT_LIMIT;
}

declare global {
  interface Window {
    /** Znacznik startu wysylki wiadomosci — czytany przy pomiarze czasu odpowiedzi. */
    lastMessageStartTime?: number;
  }
}

/** Odpowiedz z endpointu wysylki wiadomosci (JSON i SSE zwracaja ten sam ksztalt). */
type ChatSendResponse = {
  userMessage?: Message;
  assistantMessage?: Message;
  aiMessage?: Message;
  /** Encje Meta do recznych akcji (marketing-analyst) — ten sam ksztalt co entitiesByMessage. */
  entities?: Array<{
    entityType: "campaign" | "adset";
    entityId: string;
    entityName: string;
    status: string;
    dailyBudget: number | null;
    lifetimeBudget: number | null;
    spend: string;
  }>;
  meta?: {
    tokensDeducted?: number;
    tokensRemaining?: number;
    trialAiResponseCount?: number;
  };
  tokensDeducted?: number;
  tokensRemaining?: number;
};

/** Fragment cache /api/auth/me aktualizowany optymistycznie po odjeciu tokenow. */
type AuthMeCache = {
  user?: {
    tokensRemaining?: number;
    tokensUsedThisMonth?: number;
    [field: string]: unknown;
  };
  [field: string]: unknown;
};

/** Parse API error into a user-friendly message */
function parseApiErrorMessage(
  error: unknown,
  t: (key: string, opts?: Record<string, unknown>) => string
): string {
  const rawMessage = (error as { message?: unknown } | null)?.message;
  const msg = typeof rawMessage === "string" ? rawMessage : "";

  // 429 — rate limiting
  if (msg.includes("429")) {
    return t("errors.tooManyRequests");
  }

  // 413 — payload too large
  if (msg.includes("413")) {
    return t("errors.payloadTooLarge");
  }

  // 400 — validation / tokens / business logic
  if (msg.includes("400")) {
    // Try to extract JSON body from "400: {...}" format
    const jsonStart = msg.indexOf("{");
    if (jsonStart !== -1) {
      try {
        const body = JSON.parse(msg.slice(jsonStart));
        // Zod validation error with issues array
        if (body.issues && Array.isArray(body.issues)) {
          const firstIssue = body.issues[0];
          if (firstIssue?.code === "too_big") {
            return t("errors.contentTooLong", { max: firstIssue.maximum?.toLocaleString("pl-PL") });
          }
          if (firstIssue?.message) {
            return firstIssue.message;
          }
        }
        // Direct message from server
        if (body.message) {
          return body.message;
        }
      } catch {
        // Not valid JSON — fall through
      }
    }
    // Keyword-based fallback for token errors
    if (msg.includes("token") || msg.includes("Brak tokenów")) {
      return t("errors.noTokens");
    }
    return t("errors.invalidData");
  }

  // 402 — payment / tokens
  if (msg.includes("402") || msg.includes("token")) {
    return t("errors.noTokens");
  }

  // 401 — unauthorized
  if (msg.includes("401")) {
    return t("errors.sessionExpired");
  }

  // 404 — not found
  if (msg.includes("404")) {
    return t("errors.conversationNotFound");
  }

  // 410 — gone (migrated endpoint)
  if (msg.includes("410")) {
    return t("errors.endpointUpdated");
  }

  // 500+ — server errors
  if (msg.includes("500") || msg.includes("502") || msg.includes("503") || msg.includes("504")) {
    return t("errors.serverError");
  }

  // Network / fetch errors
  if (
    msg.includes("network") ||
    msg.includes("fetch") ||
    msg.includes("Failed to fetch") ||
    msg.includes("NetworkError")
  ) {
    return t("errors.networkError");
  }

  // Timeout
  if (msg.includes("timeout") || msg.includes("AbortError")) {
    return t("errors.timeout");
  }

  return t("errors.sendFailed");
}

/**
 * VP-326: kazda integracja oddaje gotowy tekst podsumowania, ktory wklejamy do
 * rozmowy. Jeden ksztalt dla siedmiu zrodel (meta, crm, google-ads, allegro, gmc,
 * baselinker, shopify) — inaczej opisywalibysmy to samo siedem razy.
 */
type IntegrationContext = { context?: string };

export default function ChatInterface() {
  const { resolvedTheme } = useTheme();
  const { t } = useTranslation("chatInterface");
  const { t: tSC } = useTranslation("storiesCreator");
  const [input, setInput] = useState("");
  const [storiesPresetsOpen, setStoriesPresetsOpen] = useState(false);
  const [, setLocation] = useLocation();
  const [selectedImage, setSelectedImage] = useState<string>("");
  const [imageDescription, setImageDescription] = useState<string>("");
  const [analystDashboardData, setAnalystDashboardData] = useState<unknown>(null);
  const [analystChatOpen, setAnalystChatOpen] = useState(false);
  const analystChatScrollRef = useRef<HTMLDivElement>(null);
  const [analystChatWidth, setAnalystChatWidth] = useState(340);
  const analystChatResizing = useRef(false);
  const [isFetchingAnalystData, setIsFetchingAnalystData] = useState(false);
  const [lastAnalysisRecommendations, setLastAnalysisRecommendations] = useState<string | null>(
    null
  );
  const [selectedFile, setSelectedFile] = useState<{
    url: string;
    name: string;
    content: string;
    type: string;
  } | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // VP-178: ref na scrollowalny obszar wiadomości — cel forwardu zdarzeń wheel
  // ze stałej stopki (przeglądarka nie propaguje scrolla do rodzeństwa).
  const messagesScrollRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [tempUserMessage, setTempUserMessage] = useState<string>("");
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [demoModalOpen, setDemoModalOpen] = useState(false);
  const [demoModalStage, setDemoModalStage] = useState<"early" | "mid">("early");
  const imageInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const sidebarInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const { user } = useAuth();
  const {
    trackChatMessage,
    trackFileUpload,
    trackConversationStarted,
    trackPromptSent,
    trackResponseReceived,
    trackPerformance,
    trackFirstSuccess,
    trackModuleUsage,
    trackEvent,
  } = useAnalytics();

  // Auto-resize textarea function
  const autoResizeTextarea = () => {
    if (textareaRef.current) {
      const textarea = textareaRef.current;
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = "auto";
      // Set the height to scrollHeight, but within min/max limits
      const minHeight = 96; // 4 lines (24px * 4)
      const maxHeight = 288; // 12 lines (24px * 12)
      const newHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight);
      textarea.style.height = `${newHeight}px`;
    }
  };

  // Auto-resize when input changes
  useEffect(() => {
    autoResizeTextarea();
  }, [input]);

  // Marketing Analyst variants (campaign analysis types)
  const MARKETING_ANALYST_VARIANTS: { id: string; name: string; description: string }[] = [
    {
      id: "ig-funnel",
      name: "IG Funnel",
      description: "Analiza lejka Instagram (profil -> DM / optin -> konwersja)",
    },
    {
      id: "fb-flash-funnel",
      name: "FB Flash Funnel",
      description: "Szybkie mikro-lejki / kampanie Facebook nastawione na szybkie testy",
    },
    { id: "vsl-funnel", name: "VSL Funnel", description: "Analiza lejka Video Sales Letter (VSL)" },
    {
      id: "ecommerce",
      name: "E-commerce",
      description: "Analiza kampanii e-commerce (sklep, product ads, katalog)",
    },
    {
      id: "saas",
      name: "SaaS",
      description: "Analiza kampanii SaaS (trial funnels, LTV, CAC, churn, retention)",
    },
  ];
  const [selectedMarketingAnalystVariant, setSelectedMarketingAnalystVariant] = useState<{
    id: string;
    name: string;
    description: string;
  }>(MARKETING_ANALYST_VARIANTS[0]);
  const [marketingAnalystModalOpen, setMarketingAnalystModalOpen] = useState<boolean>(false);
  const [integrationsDialogOpen, setIntegrationsDialogOpen] = useState<boolean>(false);
  // Meta Ads insights context (loaded via MetaConnectPanel)
  const [metaInsightsContext, setMetaInsightsContext] = useState<string | null>(null);
  // LeadConnector CRM context (loaded via GhlConnectPanel)
  const [ghlContext, setGhlContext] = useState<string | null>(null);
  // Google Ads insights context (loaded via GoogleAdsConnectPanel)
  const [googleAdsInsightsContext, setGoogleAdsInsightsContext] = useState<string | null>(null);
  // E-commerce integration contexts
  const [allegroContext, setAllegroContext] = useState<string | null>(null);
  const [gmcContext, setGmcContext] = useState<string | null>(null);
  const [baseLinkerContext, setBaseLinkerContext] = useState<string | null>(null);
  const [shopifyContext, setShopifyContext] = useState<string | null>(null);
  // Tracking context
  const [trackingContext, setTrackingContext] = useState<string | null>(null);
  // Model preset hardcoded to fast
  const selectedModel = "gawronify-fast";

  // Ad creator mode and type selection
  const [adCreatorMode, setAdCreatorMode] = useState<
    "text" | "graphic" | "video" | "library" | "pipeline" | "ugc"
  >("text");
  const [pendingGraphicPrompt, setPendingGraphicPrompt] = useState<string | undefined>(undefined);
  const [pendingGraphicLayout, setPendingGraphicLayout] = useState<string | undefined>(undefined);
  const [pendingGraphicImageModel, setPendingGraphicImageModel] = useState<string | undefined>(
    undefined
  );
  const [pendingVideoFrameUrl, setPendingVideoFrameUrl] = useState<string | undefined>(undefined);
  const [pendingVideoFrameName, setPendingVideoFrameName] = useState<string | undefined>(undefined);
  const [pendingInspirationContext, setPendingInspirationContext] = useState<
    | {
        adName: string;
        primaryText: string;
        headline: string;
        imageUrl: string;
      }
    | undefined
  >(undefined);
  const [selectedAdType, setSelectedAdType] = useState<AdType>(() => {
    const fallback = (k: string) => k;
    return (
      buildAdTypes(fallback).find((a) => a.id === "vsl-funnel-optin") ?? buildAdTypes(fallback)[0]
    );
  });

  // Business type selection for offer-builder module
  const [selectedBusinessType, setSelectedBusinessType] = useState<"online" | "local">("online");
  const [businessTypeModalOpen, setBusinessTypeModalOpen] = useState<boolean>(false);

  // ICP-from-URL: scrape a customer's website to seed the offer worksheet
  const [urlImportOpen, setUrlImportOpen] = useState(false);
  const [urlImportValue, setUrlImportValue] = useState("");
  const [urlImportLoading, setUrlImportLoading] = useState(false);
  const [urlImportError, setUrlImportError] = useState<string | null>(null);
  // VP-130: etap animacji ładowania — "scrape" (pobieranie strony) → "ai"
  // (AI uzupełnia arkusz); przełączany progress-eventem z SSE.
  const [urlImportStage, setUrlImportStage] = useState<"scrape" | "ai">("scrape");
  // VP-99: paste a ready-made ICP / offer sheet and save it straight to the selected client
  const [pasteIcpOpen, setPasteIcpOpen] = useState(false);
  const [pasteIcpValue, setPasteIcpValue] = useState("");
  const [pasteIcpSaving, setPasteIcpSaving] = useState(false);

  // Reel creator options
  const [reelWithCTA, setReelWithCTA] = useState<boolean>(false);
  const [generateDescription, setGenerateDescription] = useState<boolean>(false);
  const [useIdeaBank, setUseIdeaBank] = useState<boolean>(false);
  const [ctaAction, setCtaAction] = useState<string>("");
  const [ctaReason, setCtaReason] = useState<string>("");
  // VP-100: selected viral content category — drives the guided interview for Reels.
  const [selectedReelCategory, setSelectedReelCategory] = useState<ReelCategoryId | null>(null);
  // VP-103: "Inspiracja" — paste a TT/IG/YT link, analyze it, generate a scenario in this format.
  const [inspirationOpen, setInspirationOpen] = useState(false);
  const [inspirationUrl, setInspirationUrl] = useState("");
  const [inspirationLoading, setInspirationLoading] = useState(false);
  // VP-103: which message bubbles have their inspiration brief expanded.
  const [expandedInspirations, setExpandedInspirations] = useState<Set<string>>(new Set());
  // VP-103: full inspiration brief for the NEXT send — rides in additionalContext
  // (separate from `content`, so it bypasses the message-length limit). Ref so the
  // send mutation reads the current value without state-timing issues.
  const pendingInspirationContextRef = useRef<string | null>(null);

  // Selected offer sheet for new conversations
  const [selectedOfferSheetId, setSelectedOfferSheetId] = useState<string>("none");

  // Preview dialog for offer-sheet badge across modules — lets user verify
  // which sheet is actually attached when a client's offer-sheet looks suspicious.
  const [offerSheetPreview, setOfferSheetPreview] = useState<{
    title: string;
    content: string;
  } | null>(null);

  // Agency client selection — wired to global store
  const {
    selectedClientId: globalSelectedClientId,
    selectedClientUserId: globalSelectedClientUserId,
    clients: globalClients,
    setSelectedClient: setGlobalSelectedClient,
  } = useAgencyClients();

  // YouTube Mode workflow states
  const [youtubeStep, setYoutubeStep] = useState<"research" | "outline" | "script">("research");
  const [selectedTitle, setSelectedTitle] = useState<string>("");
  const [generatedOutline, setGeneratedOutline] = useState<string>("");

  // Clone Yourself - Manual transcription state
  const [transcriptionText, setTranscriptionText] = useState<string>("");
  // Campaign Creator state
  const [campaignStep, setCampaignStep] = useState<string>("briefing");
  const [openOptionClicked, setOpenOptionClicked] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [campaignLandingPageUrl, setCampaignLandingPageUrl] = useState<string>("");
  const [campaignCreativeAssets, setCampaignCreativeAssets] = useState<string[]>([]);
  const [campaignCreativeModel, setCampaignCreativeModel] = useState<string>("realistic");
  const [campaignCreativeLayout, setCampaignCreativeLayout] = useState<string>("1:1");
  // Legacy state (keeping for compatibility)
  const [multipleLinks, setMultipleLinks] = useState<string>("");
  const [transcriptionStatus, setTranscriptionStatus] = useState<
    "idle" | "processing" | "completed" | "error"
  >("idle");
  const [transcriptionResults, setTranscriptionResults] = useState<string>("");

  // Wistia training video state
  const [hasWatchedVideo, setHasWatchedVideo] = useState<boolean>(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("hasWatchedOfferBuilderVideo") === "true";
    }
    return false;
  });

  const {
    currentModule,
    modules,
    currentChatId,
    setCurrentChatId,
    messages,
    setMessages,
    addMessage,
    isLoading,
    setLoading,
    setWorksheetModalOpen,
    setOfferSheetModalOpen,
  } = useStore();

  const rotatingTypingMessage = useRotatingMessage(currentModule, isLoading);

  // VP-100: starting a brand-new chat (no conversation id) returns the picker to the category grid.
  useEffect(() => {
    if (!currentChatId) setSelectedReelCategory(null);
  }, [currentChatId]);

  // Pick up prompt passed from the home screen via sessionStorage
  useEffect(() => {
    const pending = sessionStorage.getItem("pendingPrompt");
    if (pending) {
      sessionStorage.removeItem("pendingPrompt");
      setInput(pending);
    }

    // Pick up inspiration link transcript from Bank Pomysłów
    const inspirationData = sessionStorage.getItem("inspirationLinkContext");
    if (inspirationData && (currentModule === "reel-creator" || currentModule === "youtube-mode")) {
      sessionStorage.removeItem("inspirationLinkContext");
      try {
        const { title, platform, transcript, url } = JSON.parse(inspirationData);
        const prefix = `Przeanalizuj ten materiał i pomóż mi stworzyć content na jego podstawie:\n\n`;
        const meta = `[${platform.toUpperCase()}] ${title || url}\n\n`;
        const maxTranscript = 3000;
        const trimmed =
          transcript.length > maxTranscript
            ? transcript.slice(0, maxTranscript) + "..."
            : transcript;
        setInput(prefix + meta + `Transkrypt:\n${trimmed}`);
      } catch {
        /* ignore parse errors */
      }
    }
  }, [currentModule]);

  // Pick up inspiration context from URL params (from ROAS World best creatives)
  useEffect(() => {
    if (currentModule !== "ad-creator") return;
    const params = new URLSearchParams(window.location.search);
    const mode = params.get("mode");
    if (mode === "graphic") {
      setAdCreatorMode("graphic");
    }
    // Deep-link from Viral Gallery "Odtwórz" — preselect layout + image model.
    // We intentionally do NOT prefill the prompt: the user shouldn't see the
    // raw backend prompt, just upload their own brand reference photo.
    const layoutParam = params.get("layout");
    const modelParam = params.get("model");
    if (layoutParam) setPendingGraphicLayout(layoutParam);
    if (modelParam) setPendingGraphicImageModel(modelParam);
    if (layoutParam || modelParam) {
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, "", cleanUrl);
    }
    if (params.get("inspiration") === "true") {
      const adName = params.get("adName") || "";
      const primaryText = params.get("primaryText") || "";
      const headline = params.get("headline") || "";
      const imageUrl = params.get("imageUrl") || "";
      if (adName || imageUrl) {
        setPendingInspirationContext({ adName, primaryText, headline, imageUrl });
      }
      // Clean URL params to prevent re-triggering
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, "", cleanUrl);
    }
  }, [currentModule]);

  const currentModuleData = modules?.find((m) => m.id === currentModule) || null;

  // Store entities from marketing-analyst responses, keyed by assistant message ID
  const [entitiesByMessage, setEntitiesByMessage] = useState<
    Record<
      string,
      Array<{
        entityType: "campaign" | "adset";
        entityId: string;
        entityName: string;
        status: string;
        dailyBudget: number | null;
        lifetimeBudget: number | null;
        spend: string;
      }>
    >
  >({});

  // Fetch assigned clients — uses global store from sidebar selector
  const OFFER_SHEET_MODULES = new Set([
    "offer-builder",
    "ad-creator",
    "vsl-creator",
    "reel-creator",
    "campaign-creator",
    "content-creator",
    "youtube-mode",
    "carousel-creator",
    "stories-creator",
  ]);
  // VP-99: ORGANIC creators must NOT show an inline "Klient" picker — the client is chosen once by
  // the global selector near the logo (source of truth). PAID creators keep their inline picker.
  const ORGANIC_MODULES = new Set([
    "reel-creator",
    "stories-creator",
    "carousel-creator",
    "youtube-mode",
    "content-creator",
    "offer-builder",
  ]);
  // VP-103: creators that expose the "Inspiracja z linku" feature (Reels + YouTube only).
  const INSPIRACJA_MODULES = new Set(["reel-creator", "youtube-mode"]);
  const isOrganicModule = ORGANIC_MODULES.has(currentModule);
  const myClients = globalClients;
  const isAgencyUser = myClients.length > 0;

  // Compatibility aliases for global client → local usage
  const selectedAdCreatorClientId = globalSelectedClientId ?? "none";
  const selectedAnalyticsClientId = globalSelectedClientId ?? "my-account";

  // Ad creator: fetch selected client's offer sheet
  const selectedAdCreatorClient = myClients.find((c) => c.id === selectedAdCreatorClientId);
  const { data: clientOfferSheetData } = useQuery<{
    offerSheet: { id: string; title: string; content: string } | null;
  }>({
    queryKey: ["client-offer-sheet", selectedAdCreatorClientId],
    queryFn: () => apiRequest("GET", `/api/my-clients/${selectedAdCreatorClientId}/offer-sheet`),
    enabled:
      OFFER_SHEET_MODULES.has(currentModule) &&
      selectedAdCreatorClientId !== "none" &&
      isAgencyUser,
    staleTime: 60_000,
  });

  // Determine forUserId for integration queries — from global store
  const selectedClient = myClients.find((c) => c.id === globalSelectedClientId);
  const forUserId = globalSelectedClientUserId ?? undefined;
  const forUserIdParam = forUserId ? `?forUserId=${forUserId}` : "";

  // Integration connection status queries (for showing connected sources)
  const { data: metaStatus } = useQuery<{ connected: boolean }>({
    queryKey: ["meta-integration-status", forUserId],
    queryFn: () => apiRequest("GET", `/api/integrations/meta/status${forUserIdParam}`),
    enabled: currentModule === "marketing-analyst",
    staleTime: 30_000,
  });
  const { data: ghlStatus } = useQuery<{ connected: boolean }>({
    queryKey: ["ghl-integration-status", forUserId],
    queryFn: () => apiRequest("GET", `/api/integrations/crm/status${forUserIdParam}`),
    enabled: currentModule === "marketing-analyst",
    staleTime: 30_000,
  });
  // VP-192: integracja Google Ads jest ukryta w UI (brak weryfikacji Google OAuth
  // → 403 access_denied przy podłączaniu). Widzi ją wyłącznie super_admin.
  const canSeeGoogleAds = canAccessGoogleAdsIntegration(user);
  const { data: googleAdsStatus } = useQuery<{ connected: boolean }>({
    queryKey: ["google-ads-integration-status", forUserId],
    queryFn: () => apiRequest("GET", `/api/integrations/google-ads/status${forUserIdParam}`),
    enabled: currentModule === "marketing-analyst" && canSeeGoogleAds,
    staleTime: 30_000,
  });
  const { data: allegroStatus } = useQuery<{ connected: boolean }>({
    queryKey: ["allegro-integration-status", forUserId],
    queryFn: () => apiRequest("GET", `/api/integrations/allegro/status${forUserIdParam}`),
    enabled: currentModule === "marketing-analyst",
    staleTime: 30_000,
  });
  const { data: gmcStatus } = useQuery<{ connected: boolean }>({
    queryKey: ["gmc-integration-status", forUserId],
    queryFn: () => apiRequest("GET", `/api/integrations/gmc/status${forUserIdParam}`),
    enabled: currentModule === "marketing-analyst",
    staleTime: 30_000,
  });
  const { data: baseLinkerStatus } = useQuery<{ connected: boolean }>({
    queryKey: ["baselinker-integration-status", forUserId],
    queryFn: () => apiRequest("GET", `/api/integrations/baselinker/status${forUserIdParam}`),
    enabled: currentModule === "marketing-analyst",
    staleTime: 30_000,
  });
  const { data: shopifyStatus } = useQuery<{ connected: boolean }>({
    queryKey: ["shopify-integration-status", forUserId],
    queryFn: () => apiRequest("GET", `/api/integrations/shopify/status${forUserIdParam}`),
    enabled: currentModule === "marketing-analyst",
    staleTime: 30_000,
  });

  // Fetch offer sheets for dropdown
  const { data: offerSheets } = useQuery<OfferSheet[]>({
    queryKey: ["/api/offer-sheets"],
  });

  // Ref to track when we're actively sending the first message in a new conversation.
  // This prevents effects from resetting loading/messages state mid-flight.
  const isSendingFirstMessageRef = useRef(false);

  // Fetch messages for current chat
  const { data: conversationMessages, isLoading: messagesLoading } = useQuery({
    queryKey: ["/api/conversations", currentChatId, "messages"],
    queryFn: () => apiRequest("GET", `/api/conversations/${currentChatId}/messages`),
    enabled: !!currentChatId && !isSendingFirstMessageRef.current,
    staleTime: 10 * 1000, // 10 seconds - prevent stale cross-module messages
    gcTime: 60 * 1000, // 1 minute - shorter cache to avoid mixing
    refetchOnMount: "always", // always refetch when conversation changes
  });

  // Show loading spinner when switching to an existing conversation (not during first message send)
  useEffect(() => {
    if (currentChatId && !isSendingFirstMessageRef.current) {
      setLoading(true);
    }
  }, [currentChatId, setLoading]);

  // Clear loading state when messages finish loading (not during first message send)
  useEffect(() => {
    if (!messagesLoading && currentChatId && !isSendingFirstMessageRef.current) {
      window.dispatchEvent(
        new CustomEvent("conversationLoaded", {
          detail: { conversationId: currentChatId },
        })
      );
      setLoading(false);
    }
    // setLoading pochodzi ze store'a i jest stabilny — dopisanie go tylko
    // przemielaloby efekt bez zmiany zachowania (stan sprzed VP-247).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messagesLoading, currentChatId]);

  // Last message payload sent — used by the in-chat "Spróbuj ponownie" button so a
  // failed send (e.g. a transient 504/network error) can be retried with one click.
  type SendMessageVars = {
    conversationId: string;
    content: string;
    imageUrl?: string;
    imageDescription?: string;
    fileUrl?: string;
    fileName?: string;
    fileContent?: string;
  };
  const lastSendVarsRef = useRef<SendMessageVars | null>(null);

  // Clear messages immediately when switching conversations to prevent cross-module mixing
  const prevChatIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (currentChatId !== prevChatIdRef.current) {
      if (!isSendingFirstMessageRef.current) {
        setMessages([]);
      }
      prevChatIdRef.current = currentChatId;
    }
  }, [currentChatId, setMessages]);

  useEffect(() => {
    if (isSendingFirstMessageRef.current) return; // Don't overwrite messages during first send
    if (conversationMessages && Array.isArray(conversationMessages)) {
      setMessages(conversationMessages.filter((m) => m && m.id));
    } else if (!currentChatId) {
      setMessages([]);
    }
  }, [conversationMessages, currentChatId, setMessages, currentModule]);

  // Only auto-scroll when new message is added (not on every change)
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const [lastMessageCount, setLastMessageCount] = useState(0);

  useEffect(() => {
    // Only auto-scroll when new message is added and user is near bottom
    if (messages.length > lastMessageCount && shouldAutoScroll) {
      const scrollToBottom = () => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
      };

      setTimeout(scrollToBottom, 100);
    }
    setLastMessageCount(messages.length);
  }, [messages.length, lastMessageCount, shouldAutoScroll]);

  // Auto-scroll analyst sidebar chat
  useEffect(() => {
    if (analystChatOpen && analystChatScrollRef.current) {
      analystChatScrollRef.current.scrollTop = analystChatScrollRef.current.scrollHeight;
    }
  }, [messages.length, analystChatOpen]);

  // Check if user is scrolled near bottom to enable/disable auto-scroll
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100;
      setShouldAutoScroll(isNearBottom);
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  // Create new chat mutation
  const createConversationMutation = useMutation({
    mutationFn: async (data: { moduleId: string; title: string }): Promise<Conversation> => {
      // eslint-disable-next-line no-console -- front nie ma loggera; celowe logowanie diagnostyczne
      console.log("Creating new conversation:", data);
      if (data.moduleId === "vsl-creator") {
        return await apiRequest("POST", "/api/vsl-creator/conversations", { title: data.title });
      } else if (data.moduleId === "marketing-analyst") {
        return await apiRequest("POST", "/api/marketing-analyst/conversations", {
          title: data.title,
        });
      } else if (data.moduleId === "reel-creator") {
        return await apiRequest("POST", "/api/reel-creator/conversations", { title: data.title });
      } else if (data.moduleId === "email-creator") {
        return await apiRequest("POST", "/api/email-creator/conversations", { title: data.title });
      } else if (data.moduleId === "campaign-creator") {
        return await apiRequest("POST", "/api/campaign-creator/conversations", {
          title: data.title,
        });
      } else if (data.moduleId === "carousel-creator") {
        return await apiRequest("POST", "/api/carousel-creator/conversations", {
          title: data.title,
        });
      } else if (data.moduleId === "stories-creator") {
        return await apiRequest("POST", "/api/stories-creator/conversations", {
          title: data.title,
        });
      }
      return await apiRequest("POST", "/api/conversations", data);
    },
    onSuccess: (newConversation) => {
      setCurrentChatId(newConversation.id);
      queryClient.invalidateQueries({ queryKey: ["/api/conversations"] });
    },
  });

  // Upload image mutation
  const uploadImageMutation = useMutation({
    mutationFn: async (data: {
      conversationId: string;
      file: File;
    }): Promise<{ imageUrl?: string; imageDescription?: string }> => {
      const formData = new FormData();
      formData.append("image", data.file);

      return (await uploadFile(
        `/api/conversations/${data.conversationId}/upload-image`,
        formData
      )) as { imageUrl?: string; imageDescription?: string };
    },
  });

  // Send message mutation
  const sendMessageMutation = useMutation({
    mutationFn: async (data: SendMessageVars): Promise<ChatSendResponse> => {
      // eslint-disable-next-line no-console -- front nie ma loggera; celowe logowanie diagnostyczne
      console.log(
        "Sending message to conversation:",
        data.conversationId,
        "content:",
        data.content
      );

      // Prepare additional context for modules
      // Resolve which offer sheet to use:
      //   - Agency user with a client picked: use that client's offer sheet
      //     (fetched separately via /api/my-clients/:id/offer-sheet — this is
      //     what the "Arkusz: ..." badge already displays). Without this, the
      //     backend falls back to the AGENCY user's own active sheet and the
      //     LLM generates content for the wrong client.
      //   - Non-agency user with an explicit pick: use the picked sheet.
      //   - Otherwise: undefined (backend uses owner's active sheet).
      const resolvedOfferSheetId = isAgencyUser
        ? (clientOfferSheetData?.offerSheet?.id ?? undefined)
        : selectedOfferSheetId !== "none"
          ? selectedOfferSheetId
          : undefined;

      let additionalContext = {};
      if (currentModule === "reel-creator" && useIdeaBank) {
        additionalContext = {
          useIdeaBank: true,
          reelWithCTA,
          generateDescription,
          ctaAction,
          ctaReason,
          selectedModel,
          offerSheetId: resolvedOfferSheetId,
          reelCategory: selectedReelCategory ?? undefined,
          inspirationContext: pendingInspirationContextRef.current ?? undefined,
        };
      } else if (currentModule === "reel-creator") {
        additionalContext = {
          reelWithCTA,
          generateDescription,
          ctaAction,
          ctaReason,
          selectedModel,
          offerSheetId: resolvedOfferSheetId,
          reelCategory: selectedReelCategory ?? undefined,
          inspirationContext: pendingInspirationContextRef.current ?? undefined,
        };
      } else if (currentModule === "youtube-mode") {
        additionalContext = {
          youtubeStep,
          selectedTitle: youtubeStep === "outline" ? selectedTitle : undefined,
          generatedOutline: youtubeStep === "script" ? generatedOutline : undefined,
          selectedModel,
          offerSheetId: resolvedOfferSheetId,
          clientId: selectedAdCreatorClientId !== "none" ? selectedAdCreatorClientId : undefined,
          inspirationContext: pendingInspirationContextRef.current ?? undefined,
        };
      } else if (currentModule === "content-creator") {
        additionalContext = {
          selectedModel,
          offerSheetId: resolvedOfferSheetId,
          clientId: selectedAdCreatorClientId !== "none" ? selectedAdCreatorClientId : undefined,
        };
      } else if (currentModule === "offer-builder") {
        // IMPORTANT: send businessType for backend variant prompt selection (online/local)
        // clientId lets the backend load the CLIENT's linked sheet (whose userId is
        // the client, not the agency) — offerSheetId alone is filtered by agency userId.
        additionalContext = {
          businessType: selectedBusinessType, // 'online' | 'local'
          selectedModel,
          offerSheetId: resolvedOfferSheetId,
          clientId: selectedAdCreatorClientId !== "none" ? selectedAdCreatorClientId : undefined,
        };
      } else if (currentModule === "ad-creator") {
        additionalContext = {
          selectedAdType: selectedAdType.id,
          selectedModel,
          clientId: selectedAdCreatorClientId !== "none" ? selectedAdCreatorClientId : undefined,
          offerSheetId: resolvedOfferSheetId,
        };
      } else if (currentModule === "marketing-analyst") {
        additionalContext = {
          chatMode: true,
          variant: selectedMarketingAnalystVariant?.id || "vsl-funnel",
          selectedModel,
          analysisContext: lastAnalysisRecommendations || undefined,
          metaInsightsContext: metaInsightsContext || undefined,
          ghlContext: ghlContext || undefined,
          googleAdsInsightsContext: googleAdsInsightsContext || undefined,
          allegroContext: allegroContext || undefined,
          gmcContext: gmcContext || undefined,
          baseLinkerContext: baseLinkerContext || undefined,
          shopifyContext: shopifyContext || undefined,
          analyzedClientName: selectedClient?.name || undefined,
          analyzedClientBusinessType: selectedClient?.businessType || undefined,
          clientAdAccountIds:
            selectedClient?.adAccounts?.filter((a) => a.platform === "meta").map((a) => a.id) ||
            undefined,
        };
      } else if (currentModule === "clone-yourself") {
        additionalContext = {
          selectedModel,
          processedLinks: true, // Mark that links are being processed for analysis
        };
      } else if (currentModule === "campaign-creator") {
        additionalContext = {
          campaignStep,
          selectedModel,
          landingPageUrl: campaignLandingPageUrl || undefined,
          offerSheetId: resolvedOfferSheetId,
          assetUrls: campaignCreativeAssets.length > 0 ? campaignCreativeAssets : undefined,
          creativeModel: campaignCreativeModel !== "realistic" ? campaignCreativeModel : undefined,
          creativeLayout: campaignCreativeLayout !== "1:1" ? campaignCreativeLayout : undefined,
        };
      } else if (currentModule === "vsl-creator") {
        additionalContext = {
          selectedModel,
          offerSheetId: resolvedOfferSheetId,
          clientId: selectedAdCreatorClientId !== "none" ? selectedAdCreatorClientId : undefined,
        };
      } else if (currentModule === "carousel-creator") {
        additionalContext = {
          selectedModel,
          offerSheetId: resolvedOfferSheetId,
          useIdeaBank,
          carouselStyleId: useStore.getState().carouselStyleId || undefined,
          inspirationContext: pendingInspirationContextRef.current ?? undefined,
        };
      } else if (currentModule === "stories-creator") {
        const s = useStore.getState();
        additionalContext = {
          selectedModel,
          offerSheetId: resolvedOfferSheetId,
          useIdeaBank,
          frameCount: s.storiesFrameCount,
          storyFlowId: s.storiesFlowId,
          backgroundMode: s.storiesBackgroundMode,
          inspirationContext: pendingInspirationContextRef.current ?? undefined,
        };
      } else {
        // Default: still send selectedModel so every module can benefit from tiering
        additionalContext = { selectedModel };
      }

      // Decide endpoint (modular migration): offer-builder & vsl-creator use dedicated routers
      let endpoint: string;
      if (currentModule === "offer-builder") {
        endpoint = `/api/offer-builder/conversations/${data.conversationId}/messages`;
      } else if (currentModule === "vsl-creator") {
        endpoint = `/api/vsl-creator/conversations/${data.conversationId}/messages`;
      } else if (currentModule === "ad-creator") {
        endpoint = `/api/ad-creator/conversations/${data.conversationId}/messages`;
      } else if (currentModule === "marketing-analyst") {
        endpoint = `/api/marketing-analyst/conversations/${data.conversationId}/messages`;
      } else if (currentModule === "reel-creator") {
        endpoint = `/api/reel-creator/conversations/${data.conversationId}/messages`;
      } else if (currentModule === "email-creator") {
        endpoint = `/api/email-creator/conversations/${data.conversationId}/messages`;
      } else if (currentModule === "campaign-creator") {
        endpoint = `/api/campaign-creator/conversations/${data.conversationId}/messages`;
      } else if (currentModule === "carousel-creator") {
        endpoint = `/api/carousel-creator/conversations/${data.conversationId}/messages`;
      } else if (currentModule === "stories-creator") {
        endpoint = `/api/stories-creator/conversations/${data.conversationId}/messages`;
      } else {
        endpoint = `/api/conversations/${data.conversationId}/messages`;
      }

      // AI generation can take 30–90s+ for long offer sheets + multi-frame outputs.
      // Default apiRequest timeout (30s) was aborting valid stories/carousel/ad
      // generations mid-flight. 180s gives headroom without hanging UI forever.
      const requestBody = {
        content: data.content,
        imageUrl: data.imageUrl,
        imageDescription: data.imageDescription,
        fileUrl: data.fileUrl,
        fileName: data.fileName,
        fileContent: data.fileContent,
        ...additionalContext,
      };

      // VSL, Ad, Offer Builder & Marketing Analyst stream over SSE (heartbeats keep
      // CloudFront from issuing a 504 on long generations — VP-144/VP-145/VP-147,
      // Marketing Analyst added in VP-200). The helper returns the same body shape
      // as the JSON endpoints, so onSuccess/onError below stay unchanged.
      if (
        currentModule === "vsl-creator" ||
        currentModule === "ad-creator" ||
        currentModule === "offer-builder" ||
        currentModule === "marketing-analyst"
      ) {
        return await apiRequestSSE("POST", endpoint, requestBody, { timeoutMs: 180_000 });
      }
      return await apiRequest("POST", endpoint, requestBody, { timeoutMs: 180_000 });
    },
    onMutate: async (variables: SendMessageVars) => {
      // Remember the payload so the error bubble's retry button can resend it.
      lastSendVarsRef.current = variables;
      // Optimistic update: Show conversation in sidebar immediately when starting to send message.
      // exact:true → invalidate ONLY the list query, NOT the messages subquery
      // (["/api/conversations", id, "messages"]). Without exact, the prefix match
      // refetched messages mid-generation and the server-saved user message rendered
      // alongside tempUserMessage → duplicate bubble.
      queryClient.invalidateQueries({ queryKey: ["/api/conversations"], exact: true });
    },
    onSuccess: (data) => {
      if (data.userMessage) addMessage(data.userMessage);
      // Support both legacy (aiMessage) and modular (assistantMessage) response shapes
      const assistant = data.aiMessage || data.assistantMessage;
      if (assistant) {
        addMessage(assistant);

        // Store entities for manual controls (marketing-analyst)
        const entities = data.entities;
        if (entities && assistant.id) {
          setEntitiesByMessage((prev) => ({ ...prev, [assistant.id]: entities }));
        }

        // After receiving assistant response, refresh conversations list so sidebar shows the new/updated conversation immediately
        try {
          queryClient.invalidateQueries({ queryKey: ["/api/conversations"] });
        } catch (e) {
          // eslint-disable-next-line no-console -- front nie ma loggera; celowe logowanie diagnostyczne
          console.warn(
            "[ChatInterface] Failed to invalidate conversations on assistant response",
            e
          );
        }

        // Track response received
        const responseLength = assistant.content ? assistant.content.length : 0;
        const responseTime = performance.now() - (window.lastMessageStartTime ?? 0) || 0;
        const tokensUsed = data.meta?.tokensDeducted || data.tokensDeducted;

        trackResponseReceived(currentModule, responseLength, responseTime, tokensUsed);

        // Track first success (first AI response in module)
        if (messages.length === 0) {
          // First message in this chat
          trackFirstSuccess(currentModule, "first_ai_response");
        }

        // Demo booking prompt triggers (trial users only)
        const isTrialUser = user && !user.subscriptionActive && !user.trialPlus;
        if (isTrialUser) {
          const aiResponseCount = data.meta?.trialAiResponseCount ?? 0;
          const tokenAllocation = user.monthlyTokenAllocation ?? 100;
          const tokensNow = user.tokensRemaining ?? 0;
          const tokensUsedPct =
            tokenAllocation > 0 ? (tokenAllocation - tokensNow) / tokenAllocation : 0;

          // Stage 1: After 3rd AI response (early engagement)
          if (aiResponseCount >= 3 && !user.hasSeenDemoPromptEarly) {
            setDemoModalStage("early");
            setDemoModalOpen(true);
            trackEvent("demo_prompt_shown", { stage: "early", ai_response_count: aiResponseCount });
            apiRequest("POST", "/api/user/mark-demo-prompt-seen", { stage: "early" }).catch(
              () => {}
            );
            queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
          }
          // Stage 2: 50%+ tokens used (mid engagement)
          if (tokensUsedPct >= 0.5 && !user.hasSeenDemoPromptMid) {
            setDemoModalStage("mid");
            setDemoModalOpen(true);
            trackEvent("demo_prompt_shown", {
              stage: "mid",
              tokens_used_pct: Math.round(tokensUsedPct * 100),
            });
            apiRequest("POST", "/api/user/mark-demo-prompt-seen", { stage: "mid" }).catch(() => {});
            queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
          }
        }

        // YouTube Mode workflow progression
        if (currentModule === "youtube-mode" && assistant.content) {
          if (youtubeStep === "research") {
            setYoutubeStep("outline");
          } else if (youtubeStep === "outline") {
            setGeneratedOutline(assistant.content);
            setYoutubeStep("script");
          }
        }
      }
      queryClient.invalidateQueries({
        queryKey: ["/api/conversations", currentChatId, "messages"],
      });
      // Refresh user data to update token count
      // Immediate optimistic token update (avoid visible lag until refetch completes)
      try {
        const meta = data.meta || {};
        const directTokensRemaining = meta.tokensRemaining ?? data.tokensRemaining;
        const tokensDeducted = meta.tokensDeducted ?? data.tokensDeducted;
        if (typeof directTokensRemaining === "number") {
          queryClient.setQueryData(["/api/auth/me"], (prev: AuthMeCache | undefined) => {
            if (!prev || !prev.user) return prev;
            const prevUser = prev.user;
            // If tokensUsedThisMonth exists, increment it by tokensDeducted when provided
            let updatedUsed = prevUser.tokensUsedThisMonth;
            if (typeof tokensDeducted === "number" && typeof updatedUsed === "number") {
              updatedUsed = updatedUsed + tokensDeducted;
            }
            return {
              ...prev,
              user: {
                ...prevUser,
                tokensRemaining: directTokensRemaining,
                ...(typeof updatedUsed === "number" ? { tokensUsedThisMonth: updatedUsed } : {}),
              },
            };
          });
        }
      } catch (e) {
        // eslint-disable-next-line no-console -- front nie ma loggera; celowe logowanie diagnostyczne
        console.warn("[ChatInterface] Failed optimistic token update", e);
      }
      // Still trigger refetch to stay authoritative (in case of race conditions)
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
    },
    onError: (error) => {
      // eslint-disable-next-line no-console -- front nie ma loggera; celowe logowanie diagnostyczne
      console.error("SendMessage mutation error:", error);
      const friendlyMsg = parseApiErrorMessage(error, (key, opts) => t(key, opts));
      const rawMsg: string = error?.message ?? String(error);
      // VP-145: never embed the raw message in the bubble — a CloudFront 502/504
      // HTML blob would render as tag soup. Sanitize to safe display text.
      const safeDetails = sanitizeErrorMessage(rawMsg);
      const isTokenError =
        rawMsg.includes("Brak tokenów") || rawMsg.includes("token") || rawMsg.includes("402");
      const chatErrorContent = isTokenError
        ? t("errors.tokenErrorContent", { details: safeDetails })
        : t("errors.genericErrorContent", { msg: friendlyMsg, details: safeDetails });
      addMessage({
        id: `error-${Date.now()}`,
        conversationId: currentChatId ?? "",
        role: "assistant",
        content: chatErrorContent,
        createdAt: new Date(),
        imageUrl: null,
        imageDescription: null,
        fileUrl: null,
        fileName: null,
        fileContent: null,
      } as unknown as Message);
      // VP-145: tokens may have been deducted server-side before the failure was
      // reported (cost charged on completion). Refresh the balance so the counter
      // doesn't show a stale value — the SSE error envelope can't carry it.
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
    },
  });

  // Retry the last failed send (wired to the in-chat error bubble button).
  // Drops any error bubbles first so the chat doesn't accumulate stale errors.
  const handleRetryLastMessage = () => {
    const vars = lastSendVarsRef.current;
    if (!vars || sendMessageMutation.isPending) return;
    setMessages(messages.filter((m) => !String(m.id).startsWith("error-")));
    sendMessageMutation.mutate(vars);
  };

  // Upload file mutation
  const uploadFileMutation = useMutation({
    mutationFn: async ({
      conversationId,
      file,
    }: {
      conversationId: string;
      file: File;
    }): Promise<{
      fileName?: string;
      fileType?: string;
      fileUrl?: string;
      fileContent?: string;
    }> => {
      const formData = new FormData();
      formData.append("file", file);
      return (await uploadFile(`/api/conversations/${conversationId}/upload-file`, formData)) as {
        fileName?: string;
        fileType?: string;
        fileUrl?: string;
        fileContent?: string;
      };
    },
    onSuccess: (data) => {
      trackFileUpload(data.fileName ?? "", data.fileType ?? "", 0);
      toast({
        title: t("fileUpload.success"),
        description: t("fileUpload.successDesc", { name: data.fileName ?? "" }),
      });
    },
    onError: (error) => {
      toast({
        title: t("fileUpload.tryAgain"),
        description: t("messages.retryDesc"),
        variant: "destructive",
      });
    },
  });

  const handleFetchAnalystData = async () => {
    setIsFetchingAnalystData(true);
    try {
      const datePreset = "last_30d";
      const contextParts: string[] = [];

      // Fetch Meta Ads data if connected
      if (metaStatus?.connected) {
        try {
          const metaData = await apiRequest<IntegrationContext>(
            "POST",
            "/api/integrations/meta/insights",
            {
              datePreset,
              forUserId,
            }
          );
          if (metaData?.context) {
            setMetaInsightsContext(metaData.context);
            contextParts.push(metaData.context);
          }
        } catch (e) {
          // eslint-disable-next-line no-console -- front nie ma loggera; celowe logowanie diagnostyczne
          console.error("[Pobierz dane] Meta error:", e);
        }
      }

      // Fetch LeadConnector data if connected
      if (ghlStatus?.connected) {
        try {
          const ghlData = await apiRequest<IntegrationContext>(
            "GET",
            `/api/integrations/crm/data${forUserIdParam}`
          );
          if (ghlData?.context) {
            setGhlContext(ghlData.context);
            contextParts.push(ghlData.context);
          }
        } catch (e) {
          // eslint-disable-next-line no-console -- front nie ma loggera; celowe logowanie diagnostyczne
          console.error("[Pobierz dane] LeadConnector error:", e);
        }
      }

      // Fetch Google Ads data if connected
      if (googleAdsStatus?.connected) {
        try {
          const gadsData = await apiRequest<IntegrationContext>(
            "POST",
            "/api/integrations/google-ads/insights",
            {
              datePreset,
              forUserId,
            }
          );
          if (gadsData?.context) {
            setGoogleAdsInsightsContext(gadsData.context);
            contextParts.push(gadsData.context);
          }
        } catch (e) {
          // eslint-disable-next-line no-console -- front nie ma loggera; celowe logowanie diagnostyczne
          console.error("[Pobierz dane] Google Ads error:", e);
        }
      }

      // Fetch Allegro data if connected
      if (allegroStatus?.connected) {
        try {
          const allegroData = await apiRequest<IntegrationContext>(
            "POST",
            "/api/integrations/allegro/insights",
            {
              datePreset,
              forUserId,
            }
          );
          if (allegroData?.context) {
            setAllegroContext(allegroData.context);
            contextParts.push(allegroData.context);
          }
        } catch (e) {
          // eslint-disable-next-line no-console -- front nie ma loggera; celowe logowanie diagnostyczne
          console.error("[Pobierz dane] Allegro error:", e);
        }
      }

      // Fetch GMC data if connected
      if (gmcStatus?.connected) {
        try {
          const gmcData = await apiRequest<IntegrationContext>(
            "POST",
            "/api/integrations/gmc/insights",
            {
              datePreset,
              forUserId,
            }
          );
          if (gmcData?.context) {
            setGmcContext(gmcData.context);
            contextParts.push(gmcData.context);
          }
        } catch (e) {
          // eslint-disable-next-line no-console -- front nie ma loggera; celowe logowanie diagnostyczne
          console.error("[Pobierz dane] GMC error:", e);
        }
      }

      // Fetch BaseLinker data if connected
      if (baseLinkerStatus?.connected) {
        try {
          const blData = await apiRequest<IntegrationContext>(
            "POST",
            "/api/integrations/baselinker/insights",
            {
              datePreset,
              forUserId,
            }
          );
          if (blData?.context) {
            setBaseLinkerContext(blData.context);
            contextParts.push(blData.context);
          }
        } catch (e) {
          // eslint-disable-next-line no-console -- front nie ma loggera; celowe logowanie diagnostyczne
          console.error("[Pobierz dane] BaseLinker error:", e);
        }
      }

      // Fetch Shopify data if connected
      if (shopifyStatus?.connected) {
        try {
          const shopifyData = await apiRequest<IntegrationContext>(
            "POST",
            "/api/integrations/shopify/insights",
            {
              datePreset,
              forUserId,
            }
          );
          if (shopifyData?.context) {
            setShopifyContext(shopifyData.context);
            contextParts.push(shopifyData.context);
          }
        } catch (e) {
          // eslint-disable-next-line no-console -- front nie ma loggera; celowe logowanie diagnostyczne
          console.error("[Pobierz dane] Shopify error:", e);
        }
      }

      if (contextParts.length > 0) {
        toast({
          title: t("analystPanel.dataFetched"),
          description: t("analystPanel.dataFetchedDesc", { count: contextParts.length }),
        });
      } else {
        toast({ title: t("analystPanel.noData"), description: t("analystPanel.noDataDesc") });
      }
    } catch (e) {
      toast({ title: t("analystPanel.tryAgain"), description: t("messages.retryDesc") });
    } finally {
      setIsFetchingAnalystData(false);
    }
  };

  const handleFileUpload = async (file: File) => {
    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "text/plain",
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
    ];

    if (!allowedTypes.includes(file.type)) {
      toast({
        title: t("messages.tryAgain"),
        description: t("fileUpload.unsupportedFormat"),
        variant: "destructive",
      });
      return;
    }

    // Validate file size (25MB)
    if (file.size > 25 * 1024 * 1024) {
      toast({
        title: t("messages.tryAgain"),
        description: t("fileUpload.tooLarge"),
        variant: "destructive",
      });
      return;
    }

    try {
      let chatId = currentChatId;

      if (!chatId) {
        // Create new conversation first
        const newConversation = await createConversationMutation.mutateAsync({
          moduleId: currentModule!,
          title: t("messages.newConversation"),
        });
        // If offer-builder, log selected businessType for debugging network payload
        if (currentModule === "offer-builder") {
          // eslint-disable-next-line no-console -- front nie ma loggera; celowe logowanie diagnostyczne
          console.log(
            "[ChatInterface] Created new offer-builder conversation with businessType",
            selectedBusinessType
          );
        }
        chatId = newConversation.id;
      }

      toast({
        title: t("fileUpload.uploading"),
        description: t("fileUpload.uploadingDesc"),
      });

      const result = await uploadFileMutation.mutateAsync({ conversationId: chatId!, file });
      setSelectedFile({
        url: result.fileUrl || "",
        name: result.fileName || file.name || "",
        content: result.fileContent || "",
        type: result.fileType || file.type || "",
      });

      toast({
        title: t("fileUpload.ready"),
        description: t("fileUpload.readyDesc", { name: result.fileName }),
      });
    } catch (error) {
      // eslint-disable-next-line no-console -- front nie ma loggera; celowe logowanie diagnostyczne
      console.error("File upload error:", error);
      toast({
        title: t("messages.tryAgain"),
        description: t("messages.retryDesc"),
        variant: "destructive",
      });
    }
  };

  const handleImageUpload = async (file: File) => {
    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast({
        title: t("messages.tryAgain"),
        description: t("imageUpload.notImage"),
        variant: "destructive",
      });
      return;
    }

    // Validate file size (25MB)
    if (file.size > 25 * 1024 * 1024) {
      toast({
        title: t("messages.tryAgain"),
        description: t("imageUpload.tooLarge"),
        variant: "destructive",
      });
      return;
    }

    try {
      let chatId = currentChatId;

      if (!chatId) {
        // Create new conversation first
        const newConversation = await createConversationMutation.mutateAsync({
          moduleId: currentModule!,
          title: t("messages.newConversation"),
        });
        if (currentModule === "offer-builder") {
          // eslint-disable-next-line no-console -- front nie ma loggera; celowe logowanie diagnostyczne
          console.log(
            "[ChatInterface] Created new offer-builder conversation with businessType",
            selectedBusinessType
          );
        }
        chatId = newConversation.id;
      }

      toast({
        title: t("imageUpload.uploading"),
        description: t("imageUpload.uploadingDesc"),
      });

      const result = await uploadImageMutation.mutateAsync({ conversationId: chatId!, file });
      setSelectedImage(result.imageUrl || "");
      setImageDescription(result.imageDescription || "");

      toast({
        title: t("imageUpload.success"),
        description: result.imageDescription
          ? t("imageUpload.successDescWithAnalysis")
          : t("imageUpload.successDesc"),
      });
    } catch (error) {
      // eslint-disable-next-line no-console -- front nie ma loggera; celowe logowanie diagnostyczne
      console.error("Image upload error:", error);
      let errorMessage = t("imageUpload.errorDefault");

      if (
        (error as Error).message?.includes("413") ||
        (error as Error).message?.includes("too large")
      ) {
        errorMessage = t("imageUpload.errorTooLarge");
      } else if ((error as Error).message?.includes("network")) {
        errorMessage = t("imageUpload.errorNetwork");
      }

      toast({
        title: t("imageUpload.tryAgain"),
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await handleImageUpload(file);
  };

  const handlePaste = async (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      if (item.type.startsWith("image/")) {
        e.preventDefault();
        const file = item.getAsFile();

        if (file) {
          await handleImageUpload(file);
        }
        break;
      }
    }
  };

  // VP-100: kick off the guided interview for the selected viral content category.
  // selectedReelCategory is already set (from the category-card click on a prior
  // render), so it is included in sendMessageMutation's additionalContext.
  const startReelInterview = () => {
    if (!selectedReelCategory) return;
    const name = t(`reelCreator.categories.${selectedReelCategory}.name`);
    handleSendMessage(
      t("reelCreator.categories.kickoff", {
        name,
        defaultValue: `Chcę nagrać rolkę w kategorii: ${name}. Poprowadź mnie krok po kroku.`,
      })
    );
  };

  // VP-103: analyze a pasted TT/IG/YT link, then kick off a format-specific scenario
  // modelled on it (re-uses handleSendMessage's override path). Clear PL error on failure.
  const analyzeInspiration = async () => {
    const url = inspirationUrl.trim();
    if (!url || inspirationLoading) return;
    setInspirationLoading(true);
    try {
      const data = (await apiRequest(
        "POST",
        "/api/inspiration/analyze",
        { url },
        { timeoutMs: 180_000 }
      )) as {
        platform: string;
        title: string | null;
        transcript: string;
        spokenTranscript?: string;
        onScreenText?: string;
        visualDescription?: string;
        editingStyle?: string;
        hookBreakdown?: {
          firstThreeSeconds?: string;
          mechanism?: string;
          retentionDevices?: string[];
        };
      };
      const formatName = (currentModule ? t(`moduleNames.${currentModule}`) : "") || "treść";
      // Compose a multi-section brief from the rich multimodal analysis (only non-empty parts).
      const speech = (data.spokenTranscript || data.transcript || "").trim();
      const sections: string[] = [`ŹRÓDŁO: ${data.title ?? "wideo"} (${data.platform})`];
      sections.push(`TRANSKRYPCJA MOWY:\n${speech || "brak mowy (materiał wizualny / muzyczny)"}`);
      if (data.onScreenText?.trim())
        sections.push(`TEKST NA EKRANIE:\n${data.onScreenText.trim()}`);
      if (data.visualDescription?.trim())
        sections.push(`OPIS WIZUALNY:\n${data.visualDescription.trim()}`);
      if (data.editingStyle?.trim()) sections.push(`STYL MONTAŻU:\n${data.editingStyle.trim()}`);
      const hb = data.hookBreakdown;
      if (hb && (hb.firstThreeSeconds?.trim() || hb.mechanism?.trim())) {
        const rd = hb.retentionDevices?.length
          ? `\nTechniki utrzymania uwagi: ${hb.retentionDevices.join("; ")}`
          : "";
        sections.push(
          `ROZBICIE HOOKA:\nPierwsze 3 sekundy: ${hb.firstThreeSeconds ?? ""}\nMechanizm: ${hb.mechanism ?? ""}${rd}`
        );
      }
      // Short visible line + bulky brief hidden inside an [[INSPIRACJA]] marker the
      // chat render strips — so the user bubble stays compact (no 5-page transcript)
      // while the AI still receives the full multimodal analysis.
      const visible = `Inspiracja → scenariusz „${formatName}" wzorowany na: ${data.title ?? "wideo"} (${data.platform}). NIE kopiuj 1:1, przerób pod moją markę.`;
      const fullBrief = `${sections.join("\n\n")}\n\nNajpierw rozłóż hook i strukturę tego materiału (mowa + tekst na ekranie + wizual + montaż), potem zaproponuj mój własny scenariusz w tym samym stylu, dopasowany do mojej oferty.`;
      // FULL brief → server via inspirationContext (no length limit, no truncation, AI-authoritative).
      pendingInspirationContextRef.current = fullBrief;
      // DISPLAY copy in `content` — capped to the module limit so the message passes
      // validation; the server strips this [[INSPIRACJA]] block and uses the full context instead.
      const limit = getContentLimit(currentModule);
      const maxBrief = Math.max(1000, limit - visible.length - 120);
      const displayBrief =
        fullBrief.length > maxBrief
          ? fullBrief.slice(0, maxBrief) + "\n…(podgląd skrócony — pełna analiza poszła do AI)"
          : fullBrief;
      const prompt = `${visible}\n\n[[INSPIRACJA]]\n${displayBrief}\n[[/INSPIRACJA]]`;
      setInspirationOpen(false);
      setInspirationUrl("");
      handleSendMessage(prompt);
    } catch (err) {
      toast({
        title: t("inspiracja.failedTitle", { defaultValue: "Nie udało się przeanalizować linku" }),
        description: err instanceof Error ? err.message.replace(/^\d+:\s*/, "") : "Błąd",
        variant: "destructive",
      });
    } finally {
      setInspirationLoading(false);
    }
  };

  const handleSendMessage = async (overrideArg?: unknown) => {
    // overrideArg may be a programmatic string (e.g. VP-100 guided-interview kickoff)
    // or a click/keyboard event when wired to onClick — only treat strings as content.
    const overrideContent = typeof overrideArg === "string" ? overrideArg : undefined;
    const rawInput = overrideContent ?? input;
    if ((!rawInput.trim() && !selectedImage) || isLoading) return;

    // Trial paywall check for offer-builder
    if (currentModule === "offer-builder" && user) {
      const hasActiveSubscription = user.subscriptionActive === true;
      const hasTrialPlus = user.trialPlus === true;
      const trialEndsAt = user.trialEndsAt;
      const isTrialActive = trialEndsAt != null && new Date(trialEndsAt) > new Date();
      if (!hasActiveSubscription && !hasTrialPlus && !isTrialActive) {
        setPaywallOpen(true);
        return;
      }
    }

    // Track performance start
    const startTime = performance.now();
    window.lastMessageStartTime = startTime;

    // Check if user has enough tokens — show paywall to upgrade
    if (user && user.tokensRemaining !== undefined && user.tokensRemaining <= 0) {
      setPaywallOpen(true);
      return;
    }

    // Client-side content length validation
    const contentLimit = getContentLimit(currentModule);
    if (rawInput.trim().length > contentLimit) {
      toast({
        title: t("messages.messageTooLong"),
        description: t("messages.messageTooLongDesc", {
          len: rawInput.trim().length.toLocaleString("pl-PL"),
          limit: contentLimit.toLocaleString("pl-PL"),
        }),
        variant: "destructive",
      });
      return;
    }

    const messageContent =
      rawInput.trim() ||
      (selectedImage
        ? t("messages.imageSent")
        : selectedFile
          ? t("messages.fileSent", { name: selectedFile.name })
          : "");

    // Reset open option state when user sends a message
    setOpenOptionClicked(false);
    setSelectedOptions([]);

    // Track prompt sent
    trackPromptSent(currentModule, messageContent.length, !!selectedImage, !!selectedFile);

    // Set temp message to show immediately in UI
    setTempUserMessage(messageContent);
    setInput("");
    setLoading(true);

    // Enable auto-scroll for new message
    setShouldAutoScroll(true);

    try {
      if (!currentChatId) {
        // Track new conversation started
        trackConversationStarted(currentModule);

        // Prevent effects from interfering during first message creation
        isSendingFirstMessageRef.current = true;

        // Create new conversation first
        const newConversation = await createConversationMutation.mutateAsync({
          moduleId: currentModule,
          title: t("messages.newConversation"),
        });

        // Send message to new conversation
        let enrichedContent = messageContent;

        if (currentModule === "offer-builder") {
          enrichedContent += `\n\n[CONTEXT: ${selectedBusinessType === "online" ? "Reklama dla biznesu online" : "Reklama dla biznesu lokalnego"}]`;
          // businessType also sent separately in body for modular endpoint variant selection
        }

        if (currentModule === "reel-creator") {
          // Add reel options context
          if (reelWithCTA) {
            enrichedContent += `\n\n[CONTEXT: Rolka z CTA]`;
            if (ctaAction) {
              enrichedContent += `\nDziałanie jakie chcemy, żeby użytkownik podjął: ${ctaAction}`;
            }
            if (ctaReason) {
              enrichedContent += `\nOpisz dlaczego ma podjąć to działanie: ${ctaReason}`;
            }
          } else {
            enrichedContent += `\n\n[CONTEXT: Rolka bez CTA - tylko hook i body]`;
          }

          if (generateDescription) {
            enrichedContent += `\n[DODATKOWE: Wygeneruj również opis do rolki i treść na okładkę]`;
          }
        }

        await sendMessageMutation.mutateAsync({
          conversationId: newConversation.id,
          content: enrichedContent,
          imageUrl: selectedImage || undefined,
          imageDescription: imageDescription || undefined,
          fileUrl: selectedFile?.url || undefined,
          fileName: selectedFile?.name || undefined,
          fileContent: selectedFile?.content || undefined,
        });
      } else {
        // Send message to existing conversation
        let enrichedContent = messageContent;

        if (currentModule === "offer-builder") {
          enrichedContent += `\n\n[CONTEXT: ${selectedBusinessType === "online" ? "Reklama dla biznesu online" : "Reklama dla biznesu lokalnego"}]`;
          // businessType also sent separately in body for modular endpoint variant selection
        }

        if (currentModule === "reel-creator") {
          // Add reel options context
          if (reelWithCTA) {
            enrichedContent += `\n\n[CONTEXT: Rolka z CTA]`;
            if (ctaAction) {
              enrichedContent += `\nDziałanie jakie chcemy, żeby użytkownik podjął: ${ctaAction}`;
            }
            if (ctaReason) {
              enrichedContent += `\nOpisz dlaczego ma podjąć to działanie: ${ctaReason}`;
            }
          } else {
            enrichedContent += `\n\n[CONTEXT: Rolka bez CTA - tylko hook i body]`;
          }

          if (generateDescription) {
            enrichedContent += `\n[DODATKOWE: Wygeneruj również opis do rolki i treść na okładkę]`;
          }
        }

        await sendMessageMutation.mutateAsync({
          conversationId: currentChatId,
          content: enrichedContent,
          imageUrl: selectedImage || undefined,
          imageDescription: imageDescription || undefined,
          fileUrl: selectedFile?.url || undefined,
          fileName: selectedFile?.name || undefined,
          fileContent: selectedFile?.content || undefined,
        });
      }

      setSelectedImage("");
      setImageDescription("");
      setSelectedFile(null);
    } catch (error) {
      // eslint-disable-next-line no-console -- front nie ma loggera; celowe logowanie diagnostyczne
      console.error("Error sending message:", error);
      const friendlyMsg = parseApiErrorMessage(error, (key, opts) => t(key, opts));
      // VP-145: sanitize before appending details so a CloudFront 502/504 HTML
      // page never lands in the toast body as raw markup.
      const safeDetails = sanitizeErrorMessage((error as Error)?.message ?? String(error));
      const fullDescription =
        safeDetails && safeDetails !== friendlyMsg
          ? `${friendlyMsg}\n\nSzczegóły błędu: ${safeDetails}`
          : friendlyMsg;
      toast({
        title: t("messages.tryAgain"),
        description: fullDescription,
        variant: "destructive",
      });
    } finally {
      isSendingFirstMessageRef.current = false;
      setLoading(false);
      setTempUserMessage(""); // Clear temp message
      pendingInspirationContextRef.current = null; // VP-103: one-shot inspiration context

      // Track performance end
      const endTime = performance.now();
      trackPerformance("send_message", endTime - startTime, {
        module_id: currentModule,
        has_image: !!selectedImage,
        has_file: !!selectedFile,
        message_length: messageContent.length,
      });

      // Auto-scroll will be handled by useEffect when messages update
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setInput(suggestion);
  };

  // Campaign Creator — handle interactive option button clicks
  const pendingOptionRef = useRef<string | null>(null);

  const handleOptionToggle = (option: CampaignOption) => {
    if (isOpenOption(option)) {
      setOpenOptionClicked(true);
      setInput("");
      setTimeout(() => textareaRef.current?.focus(), 100);
      return;
    }
    setSelectedOptions((prev) =>
      prev.includes(option.label) ? prev.filter((o) => o !== option.label) : [...prev, option.label]
    );
  };

  const handleConfirmSelectedOptions = () => {
    if (selectedOptions.length === 0) return;
    const combined = selectedOptions.join(", ");
    pendingOptionRef.current = combined;
    setInput(combined);
  };

  useEffect(() => {
    if (pendingOptionRef.current && input === pendingOptionRef.current) {
      pendingOptionRef.current = null;
      handleSendMessage();
    }
    // celowo tylko [input] — dopisanie handleSendMessage wysylaloby wiadomosc
    // przy kazdej zmianie tozsamosci handlera (stan sprzed VP-247).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input]);

  // Offer save dialog state — opened from per-message "Save" button in offer-builder
  const [offerSaveDialogContent, setOfferSaveDialogContent] = useState<string | null>(null);

  // Campaign-creator: save offer or KPI block to client or user's offer sheets
  const saveCampaignDataMutation = useMutation({
    mutationFn: async ({
      type,
      content,
      clientId,
    }: {
      type: "offer" | "kpi";
      content: string;
      clientId: string | null;
    }) => {
      const title =
        type === "offer"
          ? `Oferta — ${selectedAdCreatorClient?.name || new Date().toLocaleDateString("pl-PL")}`
          : `KPI/Budżet — ${selectedAdCreatorClient?.name || new Date().toLocaleDateString("pl-PL")}`;

      if (clientId && clientId !== "none") {
        return apiRequest("PUT", `/api/my-clients/${clientId}/offer-sheet`, { title, content });
      }
      return apiRequest("POST", "/api/offer-sheets", { title, content });
    },
    onSuccess: (_, variables) => {
      invalidateOfferSheetCaches(queryClient, variables.clientId);
      toast({
        title: t("saveCampaign.saved"),
        description:
          variables.clientId && variables.clientId !== "none"
            ? t("saveCampaign.savedDesc", { name: selectedAdCreatorClient?.name })
            : t("saveCampaign.savedToOfferSheets"),
      });
    },
    onError: () => {
      toast({
        title: t("saveCampaign.tryAgain"),
        description: t("saveCampaign.retryDesc"),
        variant: "destructive",
      });
    },
  });

  const handleSaveCampaignBlock = (block: CampaignSaveBlock) => {
    saveCampaignDataMutation.mutate({
      type: block.type,
      content: block.content,
      clientId: selectedAdCreatorClientId !== "none" ? selectedAdCreatorClientId : null,
    });
  };

  const formatTime = (date: Date | string | null) => {
    if (!date) return "";
    return new Date(date).toLocaleTimeString("pl-PL", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const hasMessages = messages.length > 0 || (isLoading && tempUserMessage);
  const isLightTheme = resolvedTheme === "light";
  const chatSurfaceClass = isLightTheme
    ? "bg-gradient-to-b from-[#f8fafc] via-[#f3f6fb] to-[#eef2f7]"
    : "bg-gradient-to-b from-[#0a0c10] via-[#0d0810] to-[#0a0c10]";
  const chatFooterClass = isLightTheme
    ? "border-black/10 bg-gradient-to-t from-[#f8fafc] via-[#f8fafc]/98 to-[#eef3f8]/92"
    : "border-white/[0.06] bg-gradient-to-t from-[#030407] via-[#030407]/98 to-[#0a0c10]/90";

  return (
    <>
      <div className={`${resolvedTheme} flex-1 flex flex-col h-full w-full bg-background`}>
        {/* Marketing Analyst Layout */}
        {currentModule === "marketing-analyst" && (
          <>
            {/* Config strip */}
            <div className="flex-shrink-0 border-b border-white/[0.08] bg-[#030407]">
              <div className="p-3">
                <div className="flex flex-wrap items-center gap-3">
                  {/* Chat AI toggle */}
                  <button
                    type="button"
                    onClick={() => setAnalystChatOpen((prev) => !prev)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      analystChatOpen
                        ? "bg-purple-600/20 text-purple-300 border border-purple-500/30"
                        : "bg-white/[0.04] text-white/50 border border-white/[0.08] hover:text-white/70"
                    }`}
                  >
                    <MessageCircle className="w-3.5 h-3.5" />
                    {t("analystPanel.chatAiButton")}
                  </button>

                  {myClients.length > 0 && (
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-red-400" />
                      <Select
                        value={selectedAnalyticsClientId}
                        onValueChange={(val) => {
                          const client = myClients.find((c) => c.id === val);
                          setGlobalSelectedClient(
                            val === "my-account"
                              ? null
                              : client
                                ? { id: client.id, userId: client.userId, name: client.name }
                                : null
                          );
                          setMetaInsightsContext(null);
                          setGhlContext(null);
                          setGoogleAdsInsightsContext(null);
                          setAllegroContext(null);
                          setGmcContext(null);
                          setBaseLinkerContext(null);
                          setShopifyContext(null);
                        }}
                      >
                        <SelectTrigger className="w-[180px] h-8 text-xs bg-[#030407] border-white/[0.1] text-white">
                          <SelectValue placeholder={t("analystPanel.selectClient")} />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0a0c10] border-white/[0.1]">
                          <SelectItem
                            value="my-account"
                            className="text-white hover:bg-white/[0.08] text-xs"
                          >
                            Moje konto
                          </SelectItem>
                          {myClients.map((c) => (
                            <SelectItem
                              key={c.id}
                              value={c.id}
                              className="text-white hover:bg-white/[0.08] text-xs"
                            >
                              {c.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {selectedClient && (
                        <span className="text-[11px] text-white/50">
                          {t(`businessTypes.${selectedClient.businessType}`) ||
                            selectedClient.businessType}
                        </span>
                      )}
                    </div>
                  )}

                  <Dialog open={integrationsDialogOpen} onOpenChange={setIntegrationsDialogOpen}>
                    <DialogTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="bg-red-600/20 border-red-500 text-red-300 hover:bg-red-600/30 text-xs h-8"
                      >
                        <Plug className="w-3 h-3 mr-1" />
                        {t("analystPanel.dataSources")}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col bg-[#030407] border-white/[0.1] text-white">
                      <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-lg">
                          <div className="w-8 h-8 bg-red-600/20 rounded-lg flex items-center justify-center">
                            <Plug className="w-4 h-4 text-red-400" />
                          </div>
                          {t("analystPanel.integrationsDialogTitle")}
                        </DialogTitle>
                        <DialogDescription>
                          {t("analystPanel.integrationsDialogDescription")}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-3 overflow-y-auto pr-1 flex-1 min-h-0 py-2">
                        <MetaConnectPanel
                          onInsightsLoaded={(ctx) => setMetaInsightsContext(ctx)}
                          forUserId={forUserId}
                        />
                        <GhlConnectPanel
                          onDataLoaded={(ctx) => setGhlContext(ctx)}
                          forUserId={forUserId}
                        />
                        {canSeeGoogleAds && (
                          <GoogleAdsConnectPanel
                            onInsightsLoaded={(ctx) => setGoogleAdsInsightsContext(ctx)}
                            forUserId={forUserId}
                          />
                        )}
                        <AllegroConnectPanel
                          onInsightsLoaded={(ctx) => setAllegroContext(ctx)}
                          forUserId={forUserId}
                        />
                        <GmcConnectPanel
                          onInsightsLoaded={(ctx) => setGmcContext(ctx)}
                          forUserId={forUserId}
                        />
                        <BaseLinkerConnectPanel
                          onInsightsLoaded={(ctx) => setBaseLinkerContext(ctx)}
                          forUserId={forUserId}
                        />
                        <ShopifyConnectPanel
                          onInsightsLoaded={(ctx) => setShopifyContext(ctx)}
                          forUserId={forUserId}
                        />
                        <TrackingConnectPanel
                          onInsightsLoaded={(ctx) => setTrackingContext(ctx)}
                          forUserId={forUserId}
                        />
                      </div>
                    </DialogContent>
                  </Dialog>

                  {/* Connected integrations dots */}
                  {[
                    { label: "Meta Ads", connected: !!metaStatus?.connected },
                    { label: "LeadConnector CRM", connected: !!ghlStatus?.connected },
                    ...(canSeeGoogleAds
                      ? [{ label: "Google Ads", connected: !!googleAdsStatus?.connected }]
                      : []),
                    { label: "Allegro", connected: !!allegroStatus?.connected },
                    { label: "GMC", connected: !!gmcStatus?.connected },
                    { label: "BaseLinker", connected: !!baseLinkerStatus?.connected },
                    { label: "Shopify", connected: !!shopifyStatus?.connected },
                    { label: "Tracking", connected: !!trackingContext },
                  ]
                    .filter((s) => s.connected)
                    .map((s) => (
                      <span
                        key={s.label}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-green-600/15 text-green-400 border border-green-500/30"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
                        {s.label}
                      </span>
                    ))}
                </div>
              </div>
            </div>

            {/* Main content: optional sidebar chat + dashboard/recommendations */}
            <div className="flex-1 flex overflow-hidden">
              {/* Optional left sidebar chat */}
              <AnimatePresence>
                {analystChatOpen && (
                  <motion.div
                    initial={{ width: 0, opacity: 0 }}
                    animate={{ width: analystChatWidth, opacity: 1 }}
                    exit={{ width: 0, opacity: 0 }}
                    transition={
                      analystChatResizing.current
                        ? { duration: 0 }
                        : { duration: 0.2, ease: "easeInOut" }
                    }
                    className="flex-shrink-0 bg-[#0a0c10] flex flex-col overflow-hidden relative"
                    style={{ minWidth: 280, maxWidth: "60vw" }}
                  >
                    {/* Resize handle */}
                    <div
                      className="absolute top-0 right-0 w-1.5 h-full cursor-col-resize z-20 group hover:bg-purple-500/30 transition-colors"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        analystChatResizing.current = true;
                        const startX = e.clientX;
                        const startWidth = analystChatWidth;
                        const onMouseMove = (ev: MouseEvent) => {
                          const newWidth = Math.max(
                            280,
                            Math.min(startWidth + (ev.clientX - startX), window.innerWidth * 0.6)
                          );
                          setAnalystChatWidth(newWidth);
                        };
                        const onMouseUp = () => {
                          analystChatResizing.current = false;
                          document.removeEventListener("mousemove", onMouseMove);
                          document.removeEventListener("mouseup", onMouseUp);
                          document.body.style.cursor = "";
                          document.body.style.userSelect = "";
                        };
                        document.body.style.cursor = "col-resize";
                        document.body.style.userSelect = "none";
                        document.addEventListener("mousemove", onMouseMove);
                        document.addEventListener("mouseup", onMouseUp);
                      }}
                    >
                      <div className="w-px h-full bg-white/[0.08] group-hover:bg-purple-500/50 ml-auto" />
                    </div>
                    {/* Sidebar header */}
                    <div className="flex items-center justify-between px-3 py-2.5 border-b border-white/[0.08]">
                      <div className="flex items-center gap-2">
                        <MessageCircle className="w-4 h-4 text-purple-400" />
                        <span className="text-sm font-medium text-white">
                          {t("analystPanel.chatAiHeader")}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setAnalystChatOpen(false)}
                        className="p-1 rounded text-white/40 hover:text-white/70 hover:bg-white/[0.06] transition-colors"
                      >
                        <XIcon className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Sidebar messages */}
                    <div
                      ref={analystChatScrollRef}
                      className="flex-1 overflow-y-auto p-3 space-y-3"
                    >
                      {messages.length === 0 && !isLoading && (
                        <div className="flex items-center justify-center h-full">
                          <p className="text-white/30 text-xs text-center px-4">
                            {t("analystPanel.askAboutCampaigns")}
                          </p>
                        </div>
                      )}
                      {messages.map((msg, i) => (
                        <div
                          key={msg.id || i}
                          className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                        >
                          <div
                            className={`max-w-[90%] rounded-lg px-3 py-2 text-xs leading-relaxed ${
                              msg.role === "user"
                                ? "bg-red-600/20 text-white/90 border border-red-500/20"
                                : "bg-white/[0.04] text-white/80 border border-white/[0.06]"
                            }`}
                          >
                            {msg.role === "assistant" ? (
                              <div className="[&_p]:my-1 [&_li]:my-0.5 [&_ul]:my-1 [&_strong]:text-white">
                                <ReactMarkdown>{msg.content}</ReactMarkdown>
                              </div>
                            ) : (
                              <span>{msg.content}</span>
                            )}
                          </div>
                        </div>
                      ))}
                      {isLoading && tempUserMessage && (
                        <>
                          <div className="flex justify-end">
                            <div className="max-w-[90%] rounded-lg px-3 py-2 text-xs bg-red-600/20 text-white/90 border border-red-500/20">
                              {tempUserMessage}
                            </div>
                          </div>
                          <div className="flex justify-start">
                            <div className="rounded-lg px-3 py-2 bg-white/[0.04] border border-white/[0.06]">
                              <Loader2 className="w-4 h-4 text-purple-400 animate-spin" />
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Sidebar input */}
                    <div className="border-t border-white/[0.08] p-3">
                      <div className="flex items-center gap-2">
                        <input
                          ref={sidebarInputRef}
                          value={input}
                          onChange={(e) => setInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault();
                              handleSendMessage();
                            }
                          }}
                          placeholder={t("analystPanel.sidebarInputPlaceholder")}
                          className="flex-1 h-8 px-3 text-xs bg-[#030407] border border-white/[0.1] rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:border-purple-500/50"
                          disabled={isLoading && !tempUserMessage}
                        />
                        <VoiceButton
                          targetRef={sidebarInputRef}
                          disabled={isLoading && !tempUserMessage}
                          className="h-8 w-8 shrink-0"
                        />
                        <button
                          type="button"
                          onClick={handleSendMessage}
                          disabled={(isLoading && !!tempUserMessage) || !input.trim()}
                          className="p-2 rounded-lg bg-purple-600/30 text-purple-300 hover:bg-purple-600/50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <Send className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Dashboard + Recommendations (centered, scrollable) */}
              <div className="flex-1 overflow-y-auto min-w-0">
                <div className="p-4 space-y-4 max-w-5xl mx-auto">
                  {analystDashboardData ? <AnalystDashboard data={analystDashboardData} /> : null}
                  <RecommendationsPanel
                    variant={selectedMarketingAnalystVariant?.id || "ig-funnel"}
                    selectedModel={selectedModel}
                    forUserId={forUserId}
                    clientName={selectedClient?.name}
                    clientAdAccountIds={selectedClient?.adAccounts?.map((a) => a.id)}
                    onAnalysisComplete={(text: string) => setLastAnalysisRecommendations(text)}
                  />
                  <AdSetActionsPanel
                    forUserId={forUserId}
                    clientAdAccountIds={selectedClient?.adAccounts
                      ?.filter((a) => a.platform === "meta")
                      .map((a) => a.id)}
                  />
                  <LeadFormSwapPanel
                    forUserId={forUserId}
                    clientAdAccountIds={selectedClient?.adAccounts
                      ?.filter((a) => a.platform === "meta")
                      .map((a) => a.id)}
                  />
                </div>
              </div>
            </div>
          </>
        )}

        {/* Messages Area - Takes available space (hidden for marketing-analyst) */}
        <div
          ref={messagesScrollRef}
          className={`flex-1 min-h-0 overflow-y-auto w-full relative ${chatSurfaceClass} ${currentModule === "marketing-analyst" ? "hidden" : ""}`}
        >
          {messagesLoading && currentChatId && !tempUserMessage ? (
            // Loading conversation messages
            <div className="flex items-center justify-center h-full p-8">
              <div className="text-center">
                <svg
                  className="w-8 h-8 animate-spin text-red-400 mx-auto mb-4"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <p className="text-white/60 text-sm">{t("messages.loadingConversation")}</p>
              </div>
            </div>
          ) : currentModule === "ad-creator" && adCreatorMode === "graphic" ? (
            // Ad Creator Graphic Mode – full-area builder
            <div className="flex-1 overflow-hidden">
              <AdGraphicsBuilder
                selectedModel={selectedModel}
                initialPrompt={pendingGraphicPrompt}
                initialLayout={pendingGraphicLayout}
                initialImageModel={pendingGraphicImageModel}
                inspirationContext={pendingInspirationContext}
              />
            </div>
          ) : currentModule === "ad-creator" && adCreatorMode === "video" ? (
            // Ad Creator Video Mode – generate AI video ads
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-2.5 text-sm text-amber-200">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>
                  <strong className="font-semibold">{t("messages.betaWarningTitle")}</strong> —{" "}
                  {t("messages.betaWarningDesc")}
                </div>
              </div>
              <AdVideoBuilder
                initialReferenceImageUrl={pendingVideoFrameUrl}
                initialReferenceImageName={pendingVideoFrameName}
              />
            </div>
          ) : currentModule === "ad-creator" && adCreatorMode === "library" ? (
            // Ad Creator Library Mode – browse saved graphics
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <GraphicsLibrary
                onEditPrompt={(prompt) => {
                  setPendingGraphicPrompt(prompt);
                  setAdCreatorMode("graphic");
                }}
                forUserId={forUserId}
                contentType="ad"
                onUseAsVideoFirstFrame={(graphic) => {
                  setPendingVideoFrameUrl(graphic.signedUrl ?? undefined);
                  setPendingVideoFrameName(graphic.filename || "Z biblioteki");
                  setAdCreatorMode("video");
                }}
              />
            </div>
          ) : currentModule === "ad-creator" && adCreatorMode === "pipeline" ? (
            // Ad Creator Pipeline Mode – creative kanban board
            <div className="flex-1 overflow-hidden">
              <CreativePipeline />
            </div>
          ) : currentModule === "ad-creator" && adCreatorMode === "ugc" ? (
            // Ad Creator UGC Studio Mode – product-to-video generator
            <div className="flex-1 flex flex-col overflow-hidden">
              <div
                className={`mx-4 mt-3 flex items-start gap-2 rounded-lg border px-4 py-2.5 text-sm flex-shrink-0 ${isLightTheme ? "border-amber-400/60 bg-amber-50 text-amber-700" : "border-amber-500/30 bg-amber-500/10 text-amber-200"}`}
              >
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>
                  <strong className="font-semibold">{t("messages.betaWarningTitle")}</strong> —{" "}
                  {t("messages.betaWarningDesc")}
                </div>
              </div>
              <div className="flex-1 overflow-hidden">
                <UgcStudioBuilder isLightTheme={isLightTheme} />
              </div>
            </div>
          ) : !hasMessages &&
            currentModule === "offer-builder" &&
            (offerSheets === undefined || offerSheets.length === 0) ? (
            // Offer Builder: no offer sheets yet — show only manage button
            <div className="flex-1 flex items-center justify-center h-full">
              <div className="text-center space-y-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto bg-white/5">
                  <FileText className="w-6 h-6 text-[#ef3e29]" />
                </div>
                <p className="text-white/60 text-sm font-medium">
                  {t("messages.emptyStateNoOfferSheets")}
                </p>
                <p className="text-white/30 text-xs max-w-xs mx-auto">
                  {t("messages.emptyStateNoOfferSheetsDesc")}
                </p>
                <button
                  type="button"
                  onClick={() => setOfferSheetModalOpen(true)}
                  className="px-4 py-2 bg-[#ef3e29] hover:bg-[#d63520] text-white text-sm font-medium rounded-lg transition-colors"
                >
                  {t("messages.manageOfferSheets")}
                </button>
              </div>
            </div>
          ) : !hasMessages && currentModule === "stories-creator" ? (
            // Stories Creator: preset gallery for fast start
            <div className="flex-1 overflow-y-auto">
              <div className="max-w-4xl mx-auto px-6 py-10 space-y-8">
                <div className="text-center space-y-3">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto bg-gradient-to-br from-[#ef3e29] to-[#d63520]">
                    <LayoutGrid className="w-7 h-7 text-white" />
                  </div>
                  <h2 className="text-white text-xl font-bold">
                    {tSC("onboarding.title", "Stwórz sekwencję Stories")}
                  </h2>
                  <p className="text-white/50 text-sm leading-relaxed max-w-xl mx-auto">
                    {tSC(
                      "presets.subtitle",
                      "Wybierz scenariusz biznesowy poniżej — automatycznie ustawimy flow, liczbę klatek i tło, a w polu wiadomości pojawi się gotowy prompt do uzupełnienia."
                    )}
                  </p>
                </div>

                {/* VP-101: presentational marquee of real generated Stories (non-interactive) */}
                <StoriesShowcaseMarquee />

                <StoriesPresetGallery onSelectPreset={(prompt) => setInput(prompt)} />

                <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[11px] text-white/55">
                  <span className="flex items-center gap-1.5">
                    <span className="w-5 h-5 rounded bg-[#ef3e29]/15 text-[#ef3e29] font-bold flex items-center justify-center text-[10px]">
                      1
                    </span>
                    {tSC("presets.flow1", "Klik preset → ustawia parametry")}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-5 h-5 rounded bg-[#ef3e29]/15 text-[#ef3e29] font-bold flex items-center justify-center text-[10px]">
                      2
                    </span>
                    {tSC("presets.flow2", "Uzupełnij placeholdery w polu wiadomości")}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-5 h-5 rounded bg-[#ef3e29]/15 text-[#ef3e29] font-bold flex items-center justify-center text-[10px]">
                      3
                    </span>
                    {tSC("presets.flow3", "(Opcjonalnie) Wgraj zdjęcia referencyjne")}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-5 h-5 rounded bg-[#ef3e29]/15 text-[#ef3e29] font-bold flex items-center justify-center text-[10px]">
                      4
                    </span>
                    {tSC("presets.flow4", "Wyślij → wygeneruj grafiki")}
                  </span>
                </div>

                <div className="bg-amber-50 dark:bg-amber-500/[0.05] border border-amber-300 dark:border-amber-500/20 rounded-xl p-3 text-center">
                  <div className="text-amber-700 dark:text-amber-200/80 text-xs leading-relaxed inline-flex items-center gap-2">
                    <Lightbulb className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                    <span>
                      <span className="font-semibold text-amber-800 dark:text-amber-300">
                        {tSC("onboarding.tipTitle", "Tip:")}
                      </span>{" "}
                      {tSC(
                        "presets.tip",
                        "Aktywny arkusz ofertowy + bank pomysłów = hooki w Twoim stylu i ze sprzedażowym kontekstem klienta."
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ) : !hasMessages ? (
            // Generic empty state with example prompts per module (Sprint 4, transcript 2026-05)
            <div className="flex-1 flex items-center justify-center h-full px-6 py-10">
              <div className="text-center max-w-2xl w-full space-y-6">
                <div>
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3 bg-gradient-to-br from-[#ef3e29]/20 to-[#d63520]/10">
                    {getModuleIcon(currentModule, "w-7 h-7", "default")}
                  </div>
                  <h2 className="text-white text-lg font-semibold mb-1">
                    {currentModule
                      ? t(`moduleNames.${currentModule}`) || currentModuleData?.name
                      : t("messages.newConversation")}
                  </h2>
                  <p className="text-white/40 text-xs">
                    {currentModule === "reel-creator"
                      ? t("reelCreator.categories.pickHint", {
                          defaultValue:
                            "Wybierz kategorię viralowego contentu — przeprowadzę Cię przez gotowy scenariusz",
                        })
                      : "Kliknij propozycję poniżej albo zacznij pisać własny prompt"}
                  </p>
                </div>
                {currentModule === "reel-creator" ? (
                  selectedReelCategory ? (
                    // VP-100: category chosen — show goal + example previews, then start the guided interview
                    (() => {
                      const cat = selectedReelCategory;
                      const examples = [0, 1];
                      return (
                        <div className="space-y-4 text-left">
                          <button
                            type="button"
                            onClick={() => setSelectedReelCategory(null)}
                            className="inline-flex items-center gap-1.5 text-[11px] text-white/50 hover:text-white/80 transition-colors"
                          >
                            <ArrowLeft className="w-3.5 h-3.5" />
                            {t("reelCreator.categories.back", {
                              defaultValue: "Wróć do kategorii",
                            })}
                          </button>
                          <div className="flex items-center gap-2.5">
                            <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-gradient-to-br from-[#ef3e29]/20 to-[#d63520]/10 flex-shrink-0">
                              {(() => {
                                const Icon = REEL_CATEGORY_ICONS[cat];
                                return <Icon className="w-4.5 h-4.5 text-[#ef3e29]" />;
                              })()}
                            </div>
                            <div className="min-w-0">
                              <h3 className="text-white text-sm font-semibold leading-tight">
                                {t(`reelCreator.categories.${cat}.name`)}
                              </h3>
                              <p className="text-white/50 text-[11px] leading-snug">
                                {t(`reelCreator.categories.${cat}.goal`)}
                              </p>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <p className="text-white/40 text-[10px] uppercase tracking-wide font-medium">
                              {t("reelCreator.categories.examplesLabel", {
                                defaultValue: "Przykłady, które mogę dla Ciebie zrobić",
                              })}
                            </p>
                            {examples.map((i) => (
                              <div
                                key={i}
                                className="p-3 rounded-lg border border-white/[0.08] bg-white/[0.02]"
                              >
                                <p className="text-[11px] font-medium text-white/80 mb-0.5">
                                  {t(`reelCreator.categories.${cat}.examples.${i}.title`)}
                                </p>
                                <p className="text-[11px] text-white/55 leading-relaxed">
                                  {t(`reelCreator.categories.${cat}.examples.${i}.scenario`)}
                                </p>
                              </div>
                            ))}
                          </div>
                          <Button
                            onClick={startReelInterview}
                            className="w-full bg-[#ef3e29] hover:bg-[#ef3e29]/90 text-white"
                          >
                            {t("reelCreator.categories.start", {
                              defaultValue: "Zacznij — zadam Ci kilka pytań",
                            })}
                          </Button>
                        </div>
                      );
                    })()
                  ) : (
                    // VP-100: viral content category grid (replaces the old 3 static reel prompts)
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {REEL_CATEGORY_IDS.map((id) => {
                        const Icon = REEL_CATEGORY_ICONS[id];
                        return (
                          <button
                            key={id}
                            onClick={() => setSelectedReelCategory(id)}
                            className="text-left p-3 rounded-xl border border-white/[0.08] bg-white/[0.02] hover:bg-white/[0.05] hover:border-[#ef3e29]/40 transition-all flex flex-col gap-1.5"
                          >
                            <div className="flex items-center gap-2">
                              <Icon className="w-4 h-4 text-[#ef3e29] flex-shrink-0" />
                              <span className="text-[11px] font-semibold text-white/85 leading-tight">
                                {t(`reelCreator.categories.${id}.name`)}
                              </span>
                            </div>
                            <p className="text-[10px] text-white/50 leading-snug">
                              {t(`reelCreator.categories.${id}.desc`)}
                            </p>
                          </button>
                        );
                      })}
                    </div>
                  )
                ) : (
                  (() => {
                    const PROMPTS_BY_MODULE: Record<string, string[]> = {
                      "content-creator": [
                        "Zaproponuj 5 hooków na rolkę o automatyzacji marketingu",
                        "Napisz post na LinkedIn z osobistą historią o porażce w biznesie",
                        "Stwórz scenariusz krótkiego edukacyjnego TikToka (do 30s)",
                      ],
                      "youtube-mode": [
                        "Research: tematy YouTube o marketingu z wysokim CPM w 2026",
                        'Skrypt 8-min do filmu: "Jak zbudowałem agencję marketingową od zera"',
                        "Tytuł i opis SEO do filmu o automatyzacji obsługi klienta",
                      ],
                      "carousel-creator": [
                        "Karuzela 7-slajdowa: framework AIDA w copywritingu",
                        "Karuzela inspirująca: przemyślenia o pracy zdalnej",
                        "Karuzela edukacyjna: jak liczyć ROAS reklam Meta",
                      ],
                    };
                    const prompts = PROMPTS_BY_MODULE[currentModule] ?? [];
                    if (prompts.length === 0) return null;
                    return (
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        {prompts.map((p, i) => (
                          <button
                            key={i}
                            onClick={() => setInput(p)}
                            className="text-left p-3 rounded-lg border border-white/[0.08] bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/[0.15] transition-all"
                          >
                            <p className="text-[11px] text-white/70 leading-relaxed">{p}</p>
                          </button>
                        ))}
                      </div>
                    );
                  })()
                )}
              </div>
            </div>
          ) : (
            // Chat Messages
            <div ref={messagesContainerRef} className="p-8 space-y-8">
              <AnimatePresence mode="popLayout">
                {messages
                  .filter((message) => message && message.id)
                  .map((message: Message, index) => (
                    <motion.div
                      key={message.id}
                      initial={{ opacity: 0, y: 20, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      transition={{
                        type: "spring",
                        stiffness: 260,
                        damping: 20,
                        delay: index * 0.05,
                      }}
                      className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
                    >
                      {message.role === "assistant" && (
                        <motion.div
                          initial={{ scale: 0, rotate: -180 }}
                          animate={{ scale: 1, rotate: 0 }}
                          transition={{
                            type: "spring",
                            stiffness: 300,
                            damping: 20,
                            delay: index * 0.05 + 0.1,
                          }}
                          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mr-4"
                        >
                          {currentModuleData?.id === "offer-builder" ? (
                            <div className="w-8 h-8 bg-gradient-to-br from-red-600 to-red-600 rounded-lg flex items-center justify-center">
                              <Package className="w-5 h-5 text-white" />
                            </div>
                          ) : currentModuleData?.id === "vsl-creator" ? (
                            <div className="w-8 h-8 bg-gradient-to-br from-red-700 to-red-600 rounded-lg flex items-center justify-center">
                              <Video className="w-5 h-5 text-white" />
                            </div>
                          ) : currentModuleData?.id === "ad-creator" ? (
                            <div className="w-8 h-8 bg-gradient-to-br from-red-600 to-cyan-600 rounded-lg flex items-center justify-center">
                              <Megaphone className="w-5 h-5 text-white" />
                            </div>
                          ) : currentModuleData?.id === "marketing-analyst" ? (
                            <div className="w-8 h-8 bg-gradient-to-br from-orange-600 to-red-600 rounded-lg flex items-center justify-center">
                              <BarChart3 className="w-5 h-5 text-white" />
                            </div>
                          ) : currentModuleData?.id === "reel-creator" ? (
                            <div className="w-8 h-8 bg-gradient-to-br from-pink-600 to-red-600 rounded-lg flex items-center justify-center">
                              <Film className="w-5 h-5 text-white" />
                            </div>
                          ) : currentModuleData?.id === "youtube-mode" ? (
                            <div className="w-8 h-8 bg-gradient-to-br from-red-600 to-red-700 rounded-lg flex items-center justify-center">
                              <Youtube className="w-5 h-5 text-white" />
                            </div>
                          ) : currentModuleData?.id === "clone-yourself" ? (
                            <div className="w-8 h-8 bg-gradient-to-br from-green-600 to-emerald-600 rounded-lg flex items-center justify-center">
                              <Users className="w-5 h-5 text-white" />
                            </div>
                          ) : currentModuleData?.id === "email-creator" ? (
                            <div className="w-8 h-8 bg-gradient-to-br from-red-600 to-red-700 rounded-lg flex items-center justify-center">
                              <Mail className="w-5 h-5 text-white" />
                            </div>
                          ) : currentModuleData?.id === "campaign-creator" ? (
                            <div className="w-8 h-8 bg-gradient-to-br from-red-600 to-orange-500 rounded-lg flex items-center justify-center">
                              <Rocket className="w-5 h-5 text-white" />
                            </div>
                          ) : currentModuleData?.id === "carousel-creator" ? (
                            <div className="w-8 h-8 bg-gradient-to-br from-violet-600 to-purple-600 rounded-lg flex items-center justify-center">
                              <LayoutGrid className="w-5 h-5 text-white" />
                            </div>
                          ) : (
                            <div
                              className={`w-8 h-8 ${currentModuleData?.color || "bg-primary"} rounded-lg flex items-center justify-center`}
                            >
                              <Package className="w-4 h-4 text-white" />
                            </div>
                          )}
                        </motion.div>
                      )}

                      <motion.div
                        initial={{ opacity: 0, x: message.role === "user" ? 20 : -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{
                          type: "spring",
                          stiffness: 200,
                          damping: 20,
                          delay: index * 0.05 + 0.15,
                        }}
                        className={`${message.role === "user" ? "max-w-2xl" : "max-w-3xl"}`}
                      >
                        <div
                          className={`
                     rounded-2xl p-6
                     ${
                       message.role === "user"
                         ? "bg-gradient-to-br from-red-600 to-red-700 !text-white rounded-br-md shadow-lg shadow-red-600/10"
                         : "bg-muted border border-border text-foreground rounded-tl-md shadow-lg"
                     }
                   `}
                        >
                          {message.imageUrl && (
                            <div className="mb-4">
                              <img
                                src={message.imageUrl}
                                alt={t("messages.attachedImageAlt")}
                                className="max-w-md rounded-xl border border-white/[0.08] shadow-lg cursor-pointer hover:shadow-xl transition-shadow"
                                onClick={() => window.open(message.imageUrl!, "_blank")}
                                onError={(e) => {
                                  // eslint-disable-next-line no-console -- front nie ma loggera; celowe logowanie diagnostyczne
                                  console.error("Error loading image:", message.imageUrl);
                                  e.currentTarget.style.display = "none";
                                }}
                                onLoad={() =>
                                  // eslint-disable-next-line no-console -- front nie ma loggera; celowe logowanie diagnostyczne
                                  console.log("Image loaded successfully:", message.imageUrl)
                                }
                              />
                            </div>
                          )}

                          {currentModule === "carousel-creator" &&
                          message.role === "assistant" &&
                          parseCarouselResponse(message.content) ? (
                            <div className="flex items-center gap-3 py-2 text-sm">
                              <LayoutGrid className="w-4 h-4 text-violet-400 shrink-0" />
                              <span className="text-white/60">
                                {t("messages.carouselGenerated", {
                                  count: parseCarouselResponse(message.content)!.slides.length,
                                })}
                              </span>
                              <span className="text-white/20">•</span>
                              <span className="text-white/30 text-xs">
                                {t("messages.carouselPreview")}
                              </span>
                            </div>
                          ) : currentModule === "stories-creator" &&
                            message.role === "assistant" &&
                            parseStoriesScript(message.content) ? (
                            <div className="space-y-3 py-2">
                              <StoryFramesPreview
                                script={parseStoriesScript(message.content)!}
                                conversationId={currentChatId ?? undefined}
                                messageId={message.id}
                              />
                            </div>
                          ) : currentModule === "stories-creator" &&
                            message.role === "assistant" &&
                            looksLikeStoriesScriptAttempt(message.content) ? (
                            /* VP-230 4a: nienaprawialny JSON → czytelny błąd + retry zamiast surowego zrzutu */
                            <div className="py-2 space-y-2" data-testid="stories-parse-error">
                              <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                                <AlertTriangle className="w-4 h-4 text-[#ef3e29] shrink-0" />
                                <span>{t("storiesCreator.parseError.title")}</span>
                              </div>
                              <p className="text-xs text-muted-foreground leading-5">
                                {t("storiesCreator.parseError.desc")}
                              </p>
                              {lastSendVarsRef.current && (
                                <button
                                  type="button"
                                  onClick={handleRetryLastMessage}
                                  disabled={sendMessageMutation.isPending}
                                  className="inline-flex items-center gap-1.5 text-xs font-medium text-[#ef3e29] hover:text-[#ef3e29]/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  data-testid="stories-parse-error-retry"
                                >
                                  <RotateCw
                                    className={`w-3.5 h-3.5 ${sendMessageMutation.isPending ? "animate-spin" : ""}`}
                                  />
                                  {t("storiesCreator.parseError.retry")}
                                </button>
                              )}
                            </div>
                          ) : (
                            <div className="leading-8 text-foreground whitespace-pre-wrap font-medium text-base space-y-2">
                              {(() => {
                                // Extract ACTION blocks from marketing-analyst chat messages
                                const isAnalystChat =
                                  currentModule === "marketing-analyst" &&
                                  message.role === "assistant";
                                const safeContent = message.content ?? "";
                                const { cleanContent: actionCleanContent, actions: chatActions } =
                                  isAnalystChat
                                    ? extractChatActions(safeContent)
                                    : {
                                        cleanContent: safeContent
                                          .replace(
                                            /\n*\[\[INSPIRACJA\]\][\s\S]*?\[\[\/INSPIRACJA\]\]/g,
                                            ""
                                          )
                                          .replace(/\n*\[CONTEXT:.*?\]/gs, "")
                                          .replace(/\n*\[DODATKOWE:.*?\]/gs, "")
                                          .trim(),
                                        actions: [] as ChatAction[],
                                      };

                                // Extract campaign-creator interactive options + save blocks
                                const isCampaignCreator =
                                  currentModule === "campaign-creator" &&
                                  message.role === "assistant";
                                const {
                                  cleanContent: saveBlocksClean,
                                  saveBlocks: campaignSaveBlocks,
                                } = isCampaignCreator
                                  ? extractCampaignSaveBlocks(actionCleanContent)
                                  : {
                                      cleanContent: actionCleanContent,
                                      saveBlocks: [] as CampaignSaveBlock[],
                                    };
                                const { cleanContent: optionsClean, options: campaignOptions } =
                                  isCampaignCreator
                                    ? extractCampaignOptions(saveBlocksClean)
                                    : {
                                        cleanContent: saveBlocksClean,
                                        options: [] as CampaignOption[],
                                      };

                                const cleanContent = optionsClean;
                                const isLastAssistantMsg =
                                  isCampaignCreator &&
                                  message.id === messages[messages.length - 1]?.id;
                                // VP-103: extract the hidden inspiration brief so it can be shown on demand.
                                const inspMatch = safeContent.match(
                                  /\[\[INSPIRACJA\]\]([\s\S]*?)\[\[\/INSPIRACJA\]\]/
                                );
                                const inspirationBrief = inspMatch ? inspMatch[1].trim() : null;
                                const inspirationExpanded = expandedInspirations.has(message.id);

                                return (
                                  <>
                                    <div className="campaign-creator-md text-sm leading-relaxed">
                                      <ReactMarkdown
                                        components={{
                                          h1: ({ children }) => (
                                            <div className="text-lg font-bold text-foreground mt-4 mb-2">
                                              {children}
                                            </div>
                                          ),
                                          h2: ({ children }) => (
                                            <div className="text-base font-bold text-foreground mt-4 mb-2">
                                              {children}
                                            </div>
                                          ),
                                          h3: ({ children }) => (
                                            <div className="text-sm font-semibold text-foreground/80 mt-3 mb-1">
                                              {children}
                                            </div>
                                          ),
                                          p: ({ children }) => (
                                            <div className="mb-2 leading-relaxed">{children}</div>
                                          ),
                                          strong: ({ children }) => (
                                            <span className="font-semibold text-foreground">
                                              {children}
                                            </span>
                                          ),
                                          em: ({ children }) => (
                                            <span className="italic text-foreground/80">
                                              {children}
                                            </span>
                                          ),
                                          ul: ({ children }) => (
                                            <ul className="space-y-1 mb-3 ml-1">{children}</ul>
                                          ),
                                          ol: ({ children }) => (
                                            <ol className="space-y-1 mb-3 ml-1 list-decimal list-inside">
                                              {children}
                                            </ol>
                                          ),
                                          li: ({ children }) => (
                                            <li className="flex items-start gap-2">
                                              <span className="text-[#ef3e29] mt-0.5 flex-shrink-0">
                                                •
                                              </span>
                                              <div className="flex-1 min-w-0">{children}</div>
                                            </li>
                                          ),
                                          code: ({ children, className }) => {
                                            const isBlock = className?.includes("language-");
                                            return isBlock ? (
                                              <pre className="bg-black/[0.06] dark:bg-white/[0.06] border border-black/[0.1] dark:border-white/[0.1] rounded-lg p-3 my-3 text-xs font-mono overflow-x-auto whitespace-pre-wrap">
                                                {children}
                                              </pre>
                                            ) : (
                                              <code className="bg-black/[0.08] dark:bg-white/[0.1] rounded px-1.5 py-0.5 text-xs font-mono text-[#ef3e29]">
                                                {children}
                                              </code>
                                            );
                                          },
                                          table: ({ children }) => (
                                            <div className="overflow-x-auto my-3">
                                              <table className="w-full text-xs border-collapse">
                                                {children}
                                              </table>
                                            </div>
                                          ),
                                          thead: ({ children }) => (
                                            <thead className="bg-black/[0.06] dark:bg-white/[0.08]">
                                              {children}
                                            </thead>
                                          ),
                                          th: ({ children }) => (
                                            <th className="px-3 py-2 text-left font-semibold text-foreground/90 border border-black/[0.1] dark:border-white/[0.1]">
                                              {children}
                                            </th>
                                          ),
                                          td: ({ children }) => (
                                            <td className="px-3 py-2 text-foreground/80 border border-black/[0.08] dark:border-white/[0.08]">
                                              {children}
                                            </td>
                                          ),
                                          tr: ({ children }) => (
                                            <tr className="even:bg-white/[0.03]">{children}</tr>
                                          ),
                                          blockquote: ({ children }) => (
                                            <blockquote className="border-l-2 border-[#ef3e29]/60 pl-3 my-2 text-muted-foreground italic">
                                              {children}
                                            </blockquote>
                                          ),
                                          hr: () => <hr className="border-border my-3" />,
                                        }}
                                      >
                                        {cleanContent}
                                      </ReactMarkdown>
                                    </div>
                                    {/* VP-147: retry button on error bubbles (504/network/timeout) */}
                                    {String(message.id).startsWith("error-") &&
                                      lastSendVarsRef.current && (
                                        <button
                                          type="button"
                                          onClick={handleRetryLastMessage}
                                          disabled={sendMessageMutation.isPending}
                                          className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-[#ef3e29] hover:text-[#ef3e29]/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                          <RotateCw
                                            className={`w-3.5 h-3.5 ${sendMessageMutation.isPending ? "animate-spin" : ""}`}
                                          />
                                          {t("errors.retry")}
                                        </button>
                                      )}
                                    {/* VP-103: collapsible inspiration analysis (transcript + visuals + edit + hook) */}
                                    {inspirationBrief && (
                                      <div className="mt-2">
                                        <button
                                          type="button"
                                          onClick={() =>
                                            setExpandedInspirations((prev) => {
                                              const next = new Set(prev);
                                              if (next.has(message.id)) next.delete(message.id);
                                              else next.add(message.id);
                                              return next;
                                            })
                                          }
                                          className="inline-flex items-center gap-1 text-[11px] text-white/70 hover:text-white transition-colors"
                                        >
                                          {inspirationExpanded ? (
                                            <ChevronUp className="w-3 h-3" />
                                          ) : (
                                            <ChevronDown className="w-3 h-3" />
                                          )}
                                          {inspirationExpanded
                                            ? "Ukryj analizę inspiracji"
                                            : "Pokaż analizę inspiracji (transkrypt, wizual, montaż, hook)"}
                                        </button>
                                        {inspirationExpanded && (
                                          <div className="mt-2 p-3 rounded-lg bg-black/25 border border-white/10 text-xs text-white/70 whitespace-pre-wrap max-h-80 overflow-y-auto">
                                            {inspirationBrief}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                    {/* Chat action buttons (marketing-analyst chat mode) */}
                                    {chatActions.length > 0 && (
                                      <div className="mt-3 pt-3 border-t border-white/[0.08] space-y-1">
                                        {chatActions.map((action, ai) => (
                                          <ChatActionButton
                                            key={`chat-action-${message.id}-${ai}`}
                                            action={action}
                                            forUserId={forUserId}
                                          />
                                        ))}
                                      </div>
                                    )}
                                    {/* Manual entity controls (marketing-analyst) */}
                                    {isAnalystChat && entitiesByMessage[message.id] && (
                                      <ManualEntityControls
                                        entities={entitiesByMessage[message.id]}
                                        forUserId={forUserId}
                                      />
                                    )}
                                    {/* Campaign Creator save blocks — KPI only */}
                                    {campaignSaveBlocks
                                      .filter((b) => b.type === "kpi")
                                      .map((block, bi) => (
                                        <CampaignKpiSaveButton
                                          key={`kpi-save-${message.id}-${bi}`}
                                          block={block}
                                          clientId={
                                            selectedAdCreatorClientId !== "none"
                                              ? selectedAdCreatorClientId
                                              : null
                                          }
                                          clientName={selectedAdCreatorClient?.name}
                                        />
                                      ))}
                                    {/* Campaign Creator interactive option buttons — multiselect, only on last assistant message */}
                                    {campaignOptions.length > 0 &&
                                      isLastAssistantMsg &&
                                      !isLoading &&
                                      !openOptionClicked && (
                                        <div className="mt-4 pt-4 border-t border-white/[0.08]">
                                          <div className="flex flex-wrap gap-2">
                                            {campaignOptions.map((opt) => {
                                              const isSelected = selectedOptions.includes(
                                                opt.label
                                              );
                                              return (
                                                <button
                                                  key={opt.id}
                                                  onClick={() => handleOptionToggle(opt)}
                                                  className={`px-4 py-2.5 rounded-xl text-sm font-medium border transition-all ${
                                                    isSelected
                                                      ? "bg-[#ef3e29]/30 border-[#ef3e29]/70 text-white"
                                                      : "bg-white/[0.06] hover:bg-[#ef3e29]/20 border-white/[0.1] hover:border-[#ef3e29]/40 text-white/80 hover:text-white"
                                                  }`}
                                                >
                                                  {isSelected && <span className="mr-1.5">✓</span>}
                                                  {opt.label}
                                                </button>
                                              );
                                            })}
                                          </div>
                                          {selectedOptions.length > 0 && (
                                            <button
                                              onClick={handleConfirmSelectedOptions}
                                              className="mt-3 px-5 py-2.5 rounded-xl text-sm font-semibold bg-[#ef3e29] hover:bg-[#d4351f] text-white transition-all"
                                            >
                                              {t("campaignCreator.confirmSelection", {
                                                count: selectedOptions.length,
                                              })}
                                            </button>
                                          )}
                                        </div>
                                      )}
                                  </>
                                );
                              })()}
                            </div>
                          )}
                        </div>

                        <div
                          className={`mt-2 ${message.role === "user" ? "text-right" : "flex items-center justify-between"}`}
                        >
                          <span className="text-xs text-white/60">
                            {formatTime(message.createdAt)}
                          </span>

                          {message.role === "assistant" && (
                            <div className="flex flex-col space-y-2 mt-3">
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => navigator.clipboard.writeText(message.content)}
                                  className="px-2 py-1 bg-white/10 hover:bg-white/[0.08] text-white rounded text-xs transition-colors"
                                  title={t("messages.copyButton")}
                                >
                                  {t("messages.copyButton")}
                                </button>
                                {currentModule === "offer-builder" && (
                                  <button
                                    onClick={() => setOfferSaveDialogContent(message.content)}
                                    className="px-3 py-1 bg-[#ef3e29] hover:bg-[#d63520] text-white rounded text-xs transition-colors font-medium"
                                    title={t("messages.saveToOfferSheet")}
                                  >
                                    {t("messages.saveToOfferSheet")}
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    </motion.div>
                  ))}
              </AnimatePresence>

              {/* Show temporary user message during loading */}
              {isLoading && tempUserMessage && (
                <AnimatePresence>
                  {/* User message */}
                  <motion.div
                    key="temp-user"
                    initial={{ opacity: 0, y: 20, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                    className="flex justify-end"
                  >
                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 }}
                      className="max-w-2xl"
                    >
                      <div className="rounded-2xl p-6 bg-gradient-to-br from-red-600 to-red-700 !text-white rounded-br-md shadow-lg shadow-red-600/10">
                        <div className="leading-7 !text-white whitespace-pre-wrap">
                          {tempUserMessage
                            .replace(/\n*\[\[INSPIRACJA\]\][\s\S]*?\[\[\/INSPIRACJA\]\]/g, "")
                            .replace(/\n*\[CONTEXT:.*?\]/gs, "")
                            .replace(/\n*\[DODATKOWE:.*?\]/gs, "")
                            .replace(/\\"/, '"')
                            .replace(/\\'/, "'")
                            .replace(/\\\\/, "\\")
                            .trim()}
                        </div>
                      </div>
                    </motion.div>
                  </motion.div>

                  {/* AI typing indicator */}
                  <motion.div
                    key="ai-typing"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="flex justify-start mt-4"
                  >
                    <div className="bg-white/[0.06] backdrop-blur-xl border border-white/[0.1] rounded-2xl rounded-bl-md p-4 ml-12">
                      <div className="flex items-center space-x-3">
                        <div className="flex items-center space-x-2">
                          <div className="animate-pulse">
                            {getModuleIcon(currentModule, "w-5 h-5", "colored")}
                          </div>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-white/70 text-sm font-medium">
                            {rotatingTypingMessage}
                          </span>
                          <div className="flex items-center gap-1 mt-1.5">
                            <div
                              className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-bounce"
                              style={{ animationDelay: "0ms" }}
                            />
                            <div
                              className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-bounce"
                              style={{ animationDelay: "150ms" }}
                            />
                            <div
                              className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-bounce"
                              style={{ animationDelay: "300ms" }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              )}

              <div ref={messagesEndRef} className="h-4" />
            </div>
          )}

          {/* Scroll to bottom button - shows when user scrolled up */}
          {hasMessages && !shouldAutoScroll && (
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              onClick={() => {
                setShouldAutoScroll(true);
                if (messagesEndRef.current) {
                  messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
                }
              }}
              className="absolute bottom-4 right-4 bg-red-600 hover:bg-red-700 text-white rounded-full p-3 shadow-lg transition-colors z-10"
            >
              <ChevronDown className="w-5 h-5" />
            </motion.button>
          )}

          {/* Loading State - Only show when no messages and no tempUserMessage */}
          {isLoading && !tempUserMessage && messages.length === 0 && (
            <div className="flex justify-start p-8">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mr-4">
                <div className="w-8 h-8 flex items-center justify-center">
                  {getModuleIcon(currentModule, "w-8 h-8", "colored")}
                </div>
              </div>
              <div className="bg-white/[0.08] backdrop-blur-sm border border-white/[0.08] rounded-2xl rounded-tl-md p-6 shadow-lg">
                <div className="flex items-center space-x-3">
                  <div className="animate-pulse">
                    {getModuleIcon(currentModule, "w-5 h-5", "colored")}
                  </div>
                  <div className="flex flex-col">
                    <span className="text-white/80 font-medium">{rotatingTypingMessage}</span>
                    <div className="flex items-center gap-1 mt-2">
                      <div
                        className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-bounce"
                        style={{ animationDelay: "0ms" }}
                      />
                      <div
                        className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-bounce"
                        style={{ animationDelay: "150ms" }}
                      />
                      <div
                        className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-bounce"
                        style={{ animationDelay: "300ms" }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Chat Input - Fixed Bottom with flex-shrink-0, scrollable when content is tall */}
        <div
          // VP-178: kółko nad stopką przewija obszar wiadomości, gdy ani stopka,
          // ani nic pod kursorem (textarea, dropdown) nie ma własnego scrolla
          // do skonsumowania w danym kierunku.
          onWheel={(e) => {
            const forward = shouldForwardWheel({
              target: e.target as HTMLElement,
              boundary: e.currentTarget,
              deltaY: e.deltaY,
            });
            const area = messagesScrollRef.current;
            if (!forward || !area) return;
            area.scrollBy({
              top: wheelDeltaToPixels({
                deltaY: e.deltaY,
                deltaMode: e.deltaMode,
                pageHeightPx: area.clientHeight,
              }),
            });
          }}
          className={`border-t ${chatFooterClass} backdrop-blur-xl z-10 w-full flex-shrink-0 ${currentModule === "ad-creator" && (adCreatorMode === "graphic" || adCreatorMode === "video" || adCreatorMode === "library" || adCreatorMode === "pipeline" || adCreatorMode === "ugc") ? "" : currentModule === "offer-builder" ? "overflow-y-auto max-h-[60vh]" : "overflow-y-auto max-h-[40vh]"} ${currentModule === "marketing-analyst" ? "hidden" : ""}`}
        >
          <div
            className={
              currentModule === "ad-creator" &&
              (adCreatorMode === "graphic" ||
                adCreatorMode === "video" ||
                adCreatorMode === "library" ||
                adCreatorMode === "pipeline" ||
                adCreatorMode === "ugc")
                ? "p-3"
                : "px-6 pt-5 pb-6"
            }
          >
            <div className="max-w-4xl mx-auto space-y-5">
              {/* VP-103: "Inspiracja z linku" — paste TT/IG/YT, analyze, generate a scenario in this format */}
              {INSPIRACJA_MODULES.has(currentModule) && (
                <div className="flex items-center justify-between p-3 bg-white/[0.04] border border-white/[0.08] rounded-lg gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Lightbulb className="w-4 h-4 text-amber-300 flex-shrink-0" />
                    <span className="text-sm font-medium text-white">
                      {t("inspiracja.label", { defaultValue: "Inspiracja" })}
                    </span>
                    <span className="text-xs text-white/40 truncate hidden sm:inline">
                      —{" "}
                      {t("inspiracja.hint", {
                        defaultValue:
                          "wklej link TikTok / Instagram / YouTube, zrobię scenariusz wzorowany na nim",
                      })}
                    </span>
                  </div>
                  <Dialog
                    open={inspirationOpen}
                    onOpenChange={(o) => {
                      setInspirationOpen(o);
                      if (!o) setInspirationUrl("");
                    }}
                  >
                    <DialogTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="bg-amber-500/15 border-amber-500/40 text-amber-200 hover:bg-amber-500/25 flex-shrink-0"
                      >
                        <Lightbulb className="w-3.5 h-3.5 mr-1.5" />{" "}
                        {t("inspiracja.give", { defaultValue: "Daj link" })}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-lg">
                      <DialogHeader>
                        <DialogTitle>
                          {t("inspiracja.dialogTitle", { defaultValue: "Inspiracja z linku" })}
                        </DialogTitle>
                        <DialogDescription>
                          {t("inspiracja.dialogDesc", {
                            defaultValue:
                              "Wklej link do TikToka, Instagrama lub YouTube. Przeanalizuję materiał (transkrypcja + hook) i zaproponuję scenariusz w tym formacie, pod Twoją markę.",
                          })}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="py-2">
                        <input
                          type="url"
                          autoFocus
                          placeholder="https://..."
                          value={inspirationUrl}
                          onChange={(e) => setInspirationUrl(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") analyzeInspiration();
                          }}
                          className="w-full px-3 py-2 bg-[#0a0c10] border border-white/[0.12] rounded-lg text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-amber-500/50"
                        />
                      </div>
                      <DialogFooter>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={inspirationLoading}
                          onClick={() => {
                            setInspirationOpen(false);
                            setInspirationUrl("");
                          }}
                        >
                          Anuluj
                        </Button>
                        <Button
                          size="sm"
                          disabled={!inspirationUrl.trim() || inspirationLoading}
                          onClick={analyzeInspiration}
                          className="bg-[#ef3e29] hover:bg-[#ef3e29]/90 text-white"
                        >
                          {inspirationLoading ? (
                            <>
                              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />{" "}
                              {t("inspiracja.analyzing", { defaultValue: "Analizuję…" })}
                            </>
                          ) : (
                            t("inspiracja.analyze", { defaultValue: "Analizuj i stwórz" })
                          )}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              )}
              {/* Ad Creator module – text/graphic mode toggle + controls */}
              {currentModule === "ad-creator" && (
                <div className="space-y-3">
                  {/* Mode toggle */}
                  <div className="flex items-center gap-1.5 p-1.5 bg-white/[0.03] border border-white/[0.06] rounded-xl">
                    <button
                      onClick={() => setAdCreatorMode("text")}
                      className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg transition-all inline-flex items-center justify-center gap-1.5 ${
                        adCreatorMode === "text"
                          ? "bg-red-600 text-white shadow-md"
                          : "text-white/50 hover:bg-white/[0.06] hover:text-white/70"
                      }`}
                    >
                      <Type size={14} strokeWidth={2} />
                      {t("adCreatorModes.text")}
                    </button>
                    <button
                      onClick={() => setAdCreatorMode("graphic")}
                      className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg transition-all inline-flex items-center justify-center gap-1.5 ${
                        adCreatorMode === "graphic"
                          ? "bg-red-600 text-white shadow-md"
                          : "text-white/50 hover:bg-white/[0.06] hover:text-white/70"
                      }`}
                    >
                      <Palette size={14} strokeWidth={2} />
                      {t("adCreatorModes.graphic")}
                    </button>
                    <button
                      onClick={() => setAdCreatorMode("library")}
                      className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg transition-all inline-flex items-center justify-center gap-1.5 ${
                        adCreatorMode === "library"
                          ? "bg-red-600 text-white shadow-md"
                          : "text-white/50 hover:bg-white/[0.06] hover:text-white/70"
                      }`}
                    >
                      <Library size={14} strokeWidth={2} />
                      {t("adCreatorModes.library")}
                    </button>
                    <button
                      onClick={() => setAdCreatorMode("video")}
                      className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg transition-all inline-flex items-center justify-center gap-1.5 ${
                        adCreatorMode === "video"
                          ? "bg-red-600 text-white shadow-md"
                          : "text-white/50 hover:bg-white/[0.06] hover:text-white/70"
                      }`}
                    >
                      <Film size={14} strokeWidth={2} />
                      {t("adCreatorModes.video")}
                    </button>
                    <button
                      onClick={() => setAdCreatorMode("pipeline")}
                      className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg transition-all inline-flex items-center justify-center gap-1.5 ${
                        adCreatorMode === "pipeline"
                          ? "bg-red-600 text-white shadow-md"
                          : "text-white/50 hover:bg-white/[0.06] hover:text-white/70"
                      }`}
                    >
                      <Workflow size={14} strokeWidth={2} />
                      {t("adCreatorModes.pipeline")}
                    </button>
                    <button
                      onClick={() => setAdCreatorMode("ugc")}
                      className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg transition-all inline-flex items-center justify-center gap-1.5 ${
                        adCreatorMode === "ugc"
                          ? "bg-red-600 text-white shadow-md"
                          : "text-white/50 hover:bg-white/[0.06] hover:text-white/70"
                      }`}
                    >
                      <Clapperboard size={14} strokeWidth={2} />
                      {t("adCreatorModes.ugcStudio")}
                    </button>
                  </div>

                  {/* Text mode: ad type selector */}
                  {adCreatorMode === "text" && (
                    <AdTypeSelector
                      selectedAdType={selectedAdType}
                      onAdTypeChange={setSelectedAdType}
                      selectedModel={selectedModel}
                    />
                  )}

                  {/* Graphic mode: builder is rendered in main content area above */}
                </div>
              )}

              {/* Campaign Creator controls — minimal: just client + landing page */}
              {currentModule === "campaign-creator" && (
                <>
                  <div className="p-3 bg-white/[0.04] border border-white/[0.08] rounded-lg space-y-3">
                    <div className="flex items-center space-x-2 mb-1">
                      <Rocket className="w-4 h-4 text-red-400" />
                      <span className="text-sm font-medium text-white">
                        {t("campaignCreator.title")}
                      </span>
                    </div>

                    {/* Client selector for campaign creator */}
                    {isAgencyUser && myClients.length > 0 && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-white/60">
                            {t("campaignCreator.clientLabel")}
                          </span>
                          <Select
                            value={selectedAdCreatorClientId}
                            onValueChange={(val) => {
                              const client = myClients.find((c) => c.id === val);
                              setGlobalSelectedClient(
                                val === "none"
                                  ? null
                                  : client
                                    ? { id: client.id, userId: client.userId, name: client.name }
                                    : null
                              );
                            }}
                          >
                            <SelectTrigger className="w-[220px] h-8 text-xs bg-[#030407] border-white/[0.1] text-white">
                              <SelectValue placeholder={t("campaignCreator.selectClient")} />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0a0c10] border-white/[0.1]">
                              <SelectItem
                                value="none"
                                className="text-white/60 hover:bg-white/[0.08] text-xs"
                              >
                                {t("campaignCreator.noClient")}
                              </SelectItem>
                              {myClients.map((c) => (
                                <SelectItem
                                  key={c.id}
                                  value={c.id}
                                  className="text-white hover:bg-white/[0.08] text-xs"
                                >
                                  {c.name} {c.businessType ? `(${c.businessType})` : ""}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        {selectedAdCreatorClientId !== "none" &&
                          clientOfferSheetData?.offerSheet && (
                            <div className="flex items-center gap-2 px-2 py-1 bg-[#ef3e29]/10 border border-[#ef3e29]/20 rounded">
                              <Package className="w-3 h-3 text-[#ef3e29] flex-shrink-0" />
                              <span className="text-[10px] text-[#ef3e29] truncate">
                                {t("campaignCreator.offerSheetLabel", {
                                  title: clientOfferSheetData.offerSheet.title,
                                })}
                              </span>
                            </div>
                          )}
                      </div>
                    )}

                    {/* Landing page URL */}
                    <div className="flex items-center space-x-2">
                      <Input
                        type="url"
                        placeholder={t("campaignCreator.landingPagePlaceholder")}
                        value={campaignLandingPageUrl}
                        onChange={(e) => setCampaignLandingPageUrl(e.target.value)}
                        className="flex-1 h-8 text-xs bg-[#030407] border-white/[0.1] text-white placeholder:text-white/40"
                      />
                    </div>
                  </div>

                  {/* Step 4 — Kreacje: asset upload panel */}
                  {campaignStep === "creatives" && (
                    <CampaignCreativeAssetsPanel
                      onAssetsChange={(urls) => setCampaignCreativeAssets(urls)}
                      onModelChange={(model) => setCampaignCreativeModel(model)}
                      onLayoutChange={(layout) => setCampaignCreativeLayout(layout)}
                      currentAssets={campaignCreativeAssets}
                      currentModel={campaignCreativeModel}
                      currentLayout={campaignCreativeLayout}
                    />
                  )}
                </>
              )}

              {/* Offer Sheet / Client selector — visible for all offer-sheet-using modules */}
              {OFFER_SHEET_MODULES.has(currentModule) &&
                !(currentModule === "ad-creator" && adCreatorMode !== "text") &&
                (isAgencyUser && myClients.length > 0 ? (
                  // VP-99: organic uses the global top selector — render no inline client picker here.
                  isOrganicModule ? null : (
                    <div className="p-3 bg-white/[0.04] border border-white/[0.08] rounded-lg space-y-2">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-red-400" />
                        <span className="text-xs font-medium text-white/70">
                          {t("offerSheetPanel.clientLabel")}
                        </span>
                      </div>
                      <Select
                        value={selectedAdCreatorClientId}
                        onValueChange={(val) => {
                          const client = myClients.find((c) => c.id === val);
                          setGlobalSelectedClient(
                            val === "none"
                              ? null
                              : client
                                ? { id: client.id, userId: client.userId, name: client.name }
                                : null
                          );
                        }}
                      >
                        <SelectTrigger className="w-full h-8 text-xs bg-[#030407] border-white/[0.1] text-white">
                          <SelectValue placeholder={t("offerSheetPanel.selectClientPlaceholder")} />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0a0c10] border-white/[0.1]">
                          <SelectItem
                            value="none"
                            className="text-white/60 hover:bg-white/[0.08] text-xs"
                          >
                            {t("offerSheetPanel.noClient")}
                          </SelectItem>
                          {myClients.map((c) => (
                            <SelectItem
                              key={c.id}
                              value={c.id}
                              className="text-white hover:bg-white/[0.08] text-xs"
                            >
                              {c.name} {c.businessType ? `(${c.businessType})` : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {selectedAdCreatorClientId !== "none" && clientOfferSheetData?.offerSheet && (
                        <button
                          type="button"
                          onClick={() =>
                            setOfferSheetPreview({
                              title: clientOfferSheetData.offerSheet!.title,
                              content: clientOfferSheetData.offerSheet!.content,
                            })
                          }
                          className="flex items-center gap-2 px-2 py-1.5 bg-[#ef3e29]/10 hover:bg-[#ef3e29]/20 border border-[#ef3e29]/20 hover:border-[#ef3e29]/40 rounded-lg transition-colors w-full text-left"
                          title={t(
                            "offerSheetPanel.previewHint",
                            "Kliknij, aby podejrzeć treść arkusza"
                          )}
                        >
                          <Package className="w-3.5 h-3.5 text-[#ef3e29] flex-shrink-0" />
                          <span className="text-xs text-[#ef3e29] truncate">
                            {t("offerSheetPanel.offerSheetLabel", {
                              title: clientOfferSheetData.offerSheet.title,
                            })}
                          </span>
                        </button>
                      )}
                      {/* VP-99: the "no offer sheet" warning moved to the global client selector. */}
                    </div>
                  )
                ) : (
                  <div className="p-3 bg-white/[0.04] border border-white/[0.08] rounded-lg space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-[#ef3e29]" />
                        <span className="text-xs font-medium text-white/70">
                          {t("offerSheetPanel.offerSheetTitle")}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        {(() => {
                          const resolved =
                            selectedOfferSheetId !== "none"
                              ? offerSheets?.find((s) => s.id === selectedOfferSheetId)
                              : offerSheets?.find((s) => s.isActive);
                          if (!resolved) return null;
                          return (
                            <button
                              type="button"
                              onClick={() =>
                                setOfferSheetPreview({
                                  title: resolved.title,
                                  content: resolved.content ?? "",
                                })
                              }
                              className="text-[10px] text-[#ef3e29] hover:text-[#d63520] transition-colors font-medium"
                            >
                              {t("offerSheetPanel.preview", "Podgląd")}
                            </button>
                          );
                        })()}
                        <button
                          type="button"
                          onClick={() => setOfferSheetModalOpen(true)}
                          className="text-[10px] text-[#ef3e29] hover:text-[#d63520] transition-colors font-medium"
                        >
                          {t("offerSheetPanel.manage")}
                        </button>
                      </div>
                    </div>
                    <Select value={selectedOfferSheetId} onValueChange={setSelectedOfferSheetId}>
                      <SelectTrigger className="w-full h-8 text-xs bg-[#030407] border-white/[0.1] text-white">
                        <SelectValue placeholder={t("offerSheetPanel.selectOfferSheet")} />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0a0c10] border-white/[0.1]">
                        <SelectItem
                          value="none"
                          className="text-white/60 hover:bg-white/[0.08] text-xs"
                        >
                          {t("offerSheetPanel.defaultOfferSheet")}
                        </SelectItem>
                        {offerSheets?.map((s) => (
                          <SelectItem
                            key={s.id}
                            value={s.id}
                            className="text-white hover:bg-white/[0.08] text-xs"
                          >
                            <span className="flex items-center gap-1.5">
                              {s.title}
                              {s.isActive && (
                                <span className="text-[9px] text-[#ef3e29]">
                                  ● {t("offerSheetPanel.active")}
                                </span>
                              )}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {offerSheets?.length === 0 && (
                      <div className="flex items-center gap-2 px-2 py-1.5 bg-[#ef3e29]/10 border border-[#ef3e29]/20 rounded-lg">
                        <AlertTriangle className="w-3.5 h-3.5 text-[#ef3e29] flex-shrink-0" />
                        <span className="text-xs text-[#ef3e29]/85">
                          {t("offerSheetPanel.noSheets")}
                        </span>
                      </div>
                    )}
                  </div>
                ))}

              {/* ICP from URL — scrape a website to seed the offer worksheet */}
              {currentModule === "offer-builder" && (
                <div className="flex items-center justify-between p-3 bg-white/[0.04] border border-white/[0.08] rounded-lg">
                  <div className="flex items-center space-x-2 min-w-0">
                    <Globe className="w-4 h-4 text-blue-300 flex-shrink-0" />
                    <span className="text-sm font-medium text-white">Idealny klient ze strony</span>
                    <span className="text-xs text-white/40 truncate">
                      — wczytaj ze strony albo wklej gotowy
                    </span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Dialog
                      open={urlImportOpen}
                      onOpenChange={(open) => {
                        setUrlImportOpen(open);
                        if (!open) {
                          setUrlImportError(null);
                          setUrlImportValue("");
                        }
                      }}
                    >
                      <DialogTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="bg-blue-500/15 border-blue-500/40 text-blue-700 hover:bg-blue-500/25 dark:text-blue-200"
                        >
                          <Globe className="w-3.5 h-3.5 mr-1.5" /> Wczytaj ze strony
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-lg">
                        <DialogHeader>
                          <DialogTitle>Wczytaj ICP ze strony</DialogTitle>
                          <DialogDescription>
                            Wklej link do strony klienta (landing, oferta, "o nas"). Wyciągniemy
                            treść i uzupełnimy arkusz idealnego klienta.
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-3 py-2">
                          <input
                            type="url"
                            autoFocus
                            placeholder="https://twoja-strona.pl"
                            value={urlImportValue}
                            onChange={(e) => setUrlImportValue(e.target.value)}
                            disabled={urlImportLoading}
                            className="w-full px-3 py-2 bg-[#0a0c10] border border-white/[0.12] rounded-lg text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-blue-500/50"
                          />
                          {urlImportError && (
                            <p className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded px-2 py-1.5">
                              {urlImportError}
                            </p>
                          )}
                          {/* VP-130: animacja scrapowania — etapy przełączane progress-eventem z SSE */}
                          {urlImportLoading ? (
                            <div className="space-y-2 rounded-lg border border-white/[0.08] bg-white/[0.04] px-3 py-2.5">
                              <div className="flex items-center gap-2 text-xs">
                                {urlImportStage === "scrape" ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-300 flex-shrink-0" />
                                ) : (
                                  <Check className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                                )}
                                <span
                                  className={
                                    urlImportStage === "scrape"
                                      ? "text-white animate-pulse"
                                      : "text-white/50"
                                  }
                                >
                                  Pobieramy treść strony…
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-xs">
                                {urlImportStage === "ai" ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-300 flex-shrink-0" />
                                ) : (
                                  <span className="w-3.5 h-3.5 rounded-full border border-white/20 flex-shrink-0" />
                                )}
                                <span
                                  className={
                                    urlImportStage === "ai"
                                      ? "text-white animate-pulse"
                                      : "text-white/40"
                                  }
                                >
                                  Uzupełniamy arkusz idealnego klienta… (to może potrwać około
                                  minuty)
                                </span>
                              </div>
                            </div>
                          ) : (
                            <p className="text-[11px] text-white/40">
                              Tip: działa najlepiej dla statycznych stron (WordPress, GHL, Webflow).
                              Strony renderowane JS-em (SPA bez SSR) mogą zwrócić pustą treść.
                            </p>
                          )}
                        </div>
                        <DialogFooter>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setUrlImportOpen(false)}
                            disabled={urlImportLoading}
                          >
                            Anuluj
                          </Button>
                          <Button
                            size="sm"
                            disabled={urlImportLoading || !urlImportValue.trim()}
                            onClick={async () => {
                              setUrlImportError(null);
                              setUrlImportLoading(true);
                              setUrlImportStage("scrape");
                              // VP-130: adres bez schematu dostaje https:// — user widzi
                              // w polu znormalizowany adres, który faktycznie pobieramy.
                              const targetUrl = normalizeWebsiteUrl(urlImportValue);
                              setUrlImportValue(targetUrl);
                              try {
                                // Ensure we have a conversation; if not, create one first so the
                                // backend can persist the scrape→AI exchange directly.
                                let convId = currentChatId;
                                if (!convId) {
                                  const conv = (await apiRequest(
                                    "POST",
                                    "/api/offer-builder/conversations",
                                    {
                                      title: "ICP ze strony",
                                    }
                                  )) as { id: string };
                                  convId = conv.id;
                                  setCurrentChatId(convId);
                                }

                                // VP-144: scrape + AI can take 30–90s+. Stream over SSE
                                // (heartbeats) so CloudFront never returns a 504 whose HTML
                                // page would render raw in this modal. Same body shape as JSON.
                                const data = (await apiRequestSSE(
                                  "POST",
                                  "/api/offer-builder/scrape-url",
                                  {
                                    url: targetUrl,
                                    conversationId: convId,
                                    businessType: selectedBusinessType,
                                    selectedModel,
                                  },
                                  {
                                    timeoutMs: 180_000,
                                    // VP-130: strona pobrana → animacja przechodzi na etap AI.
                                    onProgress: (stage) => {
                                      if (stage === "scraped") setUrlImportStage("ai");
                                    },
                                  }
                                )) as {
                                  userMessage?: Message;
                                  assistantMessage?: Message;
                                  meta?: Record<string, unknown>;
                                };

                                // Auto-flow response: drop both messages straight into chat.
                                // We always send conversationId, so the backend always returns
                                // this shape (the no-conversationId raw-scrape path is unused here).
                                if (data.userMessage && data.assistantMessage) {
                                  addMessage(data.userMessage);
                                  addMessage(data.assistantMessage);
                                  try {
                                    queryClient.invalidateQueries({
                                      queryKey: ["/api/conversations"],
                                    });
                                  } catch {
                                    /* noop */
                                  }
                                  setUrlImportOpen(false);
                                  setUrlImportValue("");
                                  toast({
                                    title: "ICP wygenerowane",
                                    description:
                                      "Strona została przeanalizowana — arkusz pojawił się w rozmowie.",
                                  });
                                } else {
                                  // Unexpected payload shape — surface an error instead of
                                  // silently leaving the spinner/modal in limbo.
                                  setUrlImportError(
                                    "Nie udało się wczytać strony — spróbuj ponownie."
                                  );
                                }
                              } catch (err) {
                                // VP-144: sanitize so a 504/HTML body never renders raw in the modal.
                                setUrlImportError(sanitizeErrorMessage(err));
                              } finally {
                                setUrlImportLoading(false);
                              }
                            }}
                          >
                            {urlImportLoading ? (
                              <>
                                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Wczytuję…
                              </>
                            ) : (
                              <>Wczytaj</>
                            )}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                    {/* VP-99: paste a ready-made ICP / offer sheet instead of generating one */}
                    <Dialog
                      open={pasteIcpOpen}
                      onOpenChange={(open) => {
                        setPasteIcpOpen(open);
                        if (!open) setPasteIcpValue("");
                      }}
                    >
                      <DialogTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="bg-white/[0.06] border-white/[0.15] text-white/80 hover:bg-white/[0.12]"
                        >
                          Wklej gotowy
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-lg">
                        <DialogHeader>
                          <DialogTitle>Wklej gotowy arkusz idealnego klienta</DialogTitle>
                          <DialogDescription>
                            {selectedAdCreatorClient
                              ? `Masz już gotowy arkusz / ICP? Wklej go — zapiszemy go od razu jako arkusz ofertowy klienta ${selectedAdCreatorClient.name}.`
                              : "Masz już gotowy arkusz / ICP? Wklej go — zapiszemy go i ustawimy jako aktywny."}
                          </DialogDescription>
                        </DialogHeader>
                        <div className="py-2">
                          <textarea
                            autoFocus
                            rows={10}
                            placeholder="Wklej tutaj gotowy arkusz idealnego klienta / ICP…"
                            value={pasteIcpValue}
                            onChange={(e) => setPasteIcpValue(e.target.value)}
                            className="w-full px-3 py-2 bg-[#0a0c10] border border-white/[0.12] rounded-lg text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-blue-500/50 resize-y"
                          />
                        </div>
                        <DialogFooter>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={pasteIcpSaving}
                            onClick={() => {
                              setPasteIcpOpen(false);
                              setPasteIcpValue("");
                            }}
                          >
                            Anuluj
                          </Button>
                          <Button
                            size="sm"
                            disabled={!pasteIcpValue.trim() || pasteIcpSaving}
                            onClick={async () => {
                              setPasteIcpSaving(true);
                              try {
                                const clientId =
                                  selectedAdCreatorClientId !== "none"
                                    ? selectedAdCreatorClientId
                                    : null;
                                const clientName = selectedAdCreatorClient?.name;
                                const title = clientName
                                  ? `Arkusz idealnego klienta — ${clientName}`
                                  : "Arkusz idealnego klienta";
                                // 1) create the offer sheet, 2) attach it to the selected client (or activate for solo)
                                const sheet = (await apiRequest("POST", "/api/offer-sheets", {
                                  title,
                                  content: pasteIcpValue.trim(),
                                })) as { id: string };
                                if (clientId) {
                                  await apiRequest("PUT", `/api/agency/clients/${clientId}`, {
                                    offerSheetId: sheet.id,
                                  });
                                } else {
                                  await apiRequest(
                                    "PUT",
                                    `/api/offer-sheets/${sheet.id}/activate`,
                                    {}
                                  );
                                }
                                queryClient.invalidateQueries({ queryKey: ["my-clients"] });
                                queryClient.invalidateQueries({
                                  queryKey: ["/api/agency/clients"],
                                });
                                // VP-94: odśwież KOMPLET pickerów arkusza (lista solo + picker klienta + podgląd).
                                invalidateOfferSheetCaches(queryClient, clientId);
                                setPasteIcpOpen(false);
                                setPasteIcpValue("");
                                toast({
                                  title: "Arkusz zapisany",
                                  description: clientName
                                    ? `Zapisano jako arkusz ofertowy klienta ${clientName}.`
                                    : "Arkusz ofertowy zapisany i aktywny.",
                                });
                              } catch (err) {
                                toast({
                                  title: "Nie udało się zapisać",
                                  description:
                                    err instanceof Error
                                      ? sanitizeErrorMessage(err)
                                      : "Błąd zapisu",
                                  variant: "destructive",
                                });
                              } finally {
                                setPasteIcpSaving(false);
                              }
                            }}
                          >
                            {pasteIcpSaving ? (
                              <>
                                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Zapisuję…
                              </>
                            ) : (
                              "Zapisz arkusz"
                            )}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              )}

              {/* Business Type Modal Trigger for offer-builder module */}
              {currentModule === "offer-builder" && (
                <div className="flex items-center justify-between p-3 bg-white/[0.04] border border-white/[0.08] rounded-lg">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-white">
                      {t("businessTypePanel.label")}
                    </span>
                    <span className="text-sm text-red-300 font-medium">
                      {selectedBusinessType === "online"
                        ? t("businessTypePanel.online")
                        : t("businessTypePanel.local")}
                    </span>
                  </div>
                  <Dialog open={businessTypeModalOpen} onOpenChange={setBusinessTypeModalOpen}>
                    <DialogTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="bg-red-600/20 border-red-500 text-red-300 hover:bg-red-600/30"
                      >
                        {t("businessTypePanel.change")}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-2xl">
                      <DialogHeader>
                        <DialogTitle>{t("businessTypePanel.dialogTitle")}</DialogTitle>
                        <DialogDescription>
                          {t("businessTypePanel.dialogDescription")}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-3 py-4">
                        <div
                          className={`p-4 rounded-lg border cursor-pointer transition-all relative ${
                            selectedBusinessType === "online"
                              ? "border-red-400 bg-red-500 shadow-md"
                              : "border-white/[0.12] bg-[#0a0c10] hover:border-white/[0.15] hover:bg-white/[0.08]"
                          }`}
                          onClick={() => {
                            setSelectedBusinessType("online");
                            setBusinessTypeModalOpen(false);
                          }}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="text-white font-semibold">
                                {t("businessTypePanel.online")}
                              </h4>
                              <p
                                className={`text-sm mt-1 ${
                                  selectedBusinessType === "online"
                                    ? "text-red-100"
                                    : "text-white/70"
                                }`}
                              >
                                Dla ekspertów online, coachów, biznesów usługowych online
                              </p>
                              <p
                                className={`text-xs mt-2 ${
                                  selectedBusinessType === "online"
                                    ? "text-red-50"
                                    : "text-white/60"
                                }`}
                              >
                                Dla ekspertów online, coachy, biznesów usługowych online itp.
                              </p>
                            </div>
                            {selectedBusinessType === "online" && (
                              <div className="ml-4 flex-shrink-0">
                                <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center">
                                  <svg
                                    className="w-4 h-4 text-red-500"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        <div
                          className={`p-4 rounded-lg border cursor-pointer transition-all relative ${
                            selectedBusinessType === "local"
                              ? "border-red-400 bg-red-500 shadow-md"
                              : "border-white/[0.12] bg-[#0a0c10] hover:border-white/[0.15] hover:bg-white/[0.08]"
                          }`}
                          onClick={() => {
                            setSelectedBusinessType("local");
                            setBusinessTypeModalOpen(false);
                          }}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="text-white font-semibold">
                                {t("businessTypePanel.local")}
                              </h4>
                              <p
                                className={`text-sm mt-1 ${
                                  selectedBusinessType === "local"
                                    ? "text-red-100"
                                    : "text-white/70"
                                }`}
                              >
                                Reklamy dla firm lokalnych
                              </p>
                              <p
                                className={`text-xs mt-2 ${
                                  selectedBusinessType === "local" ? "text-red-50" : "text-white/60"
                                }`}
                              >
                                Dla salonów, restauracji, gabinetek, usług lokalnych
                              </p>
                            </div>
                            {selectedBusinessType === "local" && (
                              <div className="ml-4 flex-shrink-0">
                                <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center">
                                  <svg
                                    className="w-4 h-4 text-red-500"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              )}

              {currentModule === "reel-creator" && (
                <div className="flex flex-wrap items-center gap-2 p-2 bg-white/[0.04] border border-white/[0.08] rounded-lg text-xs">
                  {/* Quick toggles in compact format */}
                  <div className="flex space-x-1">
                    <button
                      onClick={() => setReelWithCTA(false)}
                      className={`px-2 py-1 text-xs rounded transition-colors ${
                        !reelWithCTA
                          ? "bg-red-600 text-white"
                          : "bg-white/[0.08] text-white/70 hover:bg-white/10"
                      }`}
                    >
                      {t("reelCreator.withoutCta")}
                    </button>
                    <button
                      onClick={() => setReelWithCTA(true)}
                      className={`px-2 py-1 text-xs rounded transition-colors ${
                        reelWithCTA
                          ? "bg-red-600 text-white"
                          : "bg-white/[0.08] text-white/70 hover:bg-white/10"
                      }`}
                    >
                      {t("reelCreator.withCta")}
                    </button>
                  </div>

                  <div className="flex items-center space-x-1">
                    <Checkbox
                      id="generate-desc-compact"
                      checked={generateDescription}
                      onCheckedChange={(checked) => setGenerateDescription(checked === true)}
                      className="border-white/[0.1] data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600 h-3 w-3"
                    />
                    <Label
                      htmlFor="generate-desc-compact"
                      className="text-xs text-white cursor-pointer"
                    >
                      {t("reelCreator.description")}
                    </Label>
                  </div>

                  {/* VP-100: start a fresh reel — resets to the category grid */}
                  {(currentChatId || messages.length > 0) && (
                    <button
                      type="button"
                      onClick={() => {
                        setCurrentChatId(null);
                        setMessages([]);
                        setSelectedReelCategory(null);
                        setInput("");
                      }}
                      className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded bg-[#ef3e29]/15 text-[#ef3e29] hover:bg-[#ef3e29]/25 border border-[#ef3e29]/30 transition-colors"
                    >
                      <ArrowLeft className="w-3.5 h-3.5" />
                      {t("reelCreator.categories.newReel", { defaultValue: "Nowa rolka" })}
                    </button>
                  )}
                </div>
              )}

              {/* Clone Yourself - Manual Transcription Interface */}
              {currentModule === "clone-yourself" && (
                <div className="p-4 bg-gradient-to-br from-green-900/20 to-emerald-900/20 border border-green-600/30 rounded-lg space-y-4">
                  <div className="flex items-center space-x-2">
                    <UserPlus className="w-4 h-4 text-green-400" />
                    <span className="text-sm font-medium text-green-200">
                      {t("cloneYourself.title")}
                    </span>
                  </div>

                  <Textarea
                    placeholder={t("cloneYourself.textareaPlaceholder")}
                    value={transcriptionText}
                    onChange={(e) => setTranscriptionText(e.target.value)}
                    className="min-h-[150px] bg-white/[0.04] border-green-600/30 text-green-200 placeholder-green-400/60 resize-none"
                  />

                  <div className="flex items-center justify-between">
                    <p className="text-xs text-green-400/80">{t("cloneYourself.hint")}</p>
                    <span className="text-xs text-green-400">
                      {t("cloneYourself.charCount", { count: transcriptionText.length })}
                    </span>
                  </div>

                  <Button
                    onClick={() => {
                      if (transcriptionText.trim()) {
                        setInput(`Przeanalizuj mój styl komunikacji na podstawie poniższych transkrypcji i zapamiętaj go dla dalszych rozmów:

${transcriptionText.trim()}

Zidentyfikuj moje charakterystyczne cechy językowe, ton, sposób budowania zdań, używane zwroty i emocjonalność przekazu. Dzięki tej analizie będziesz mógł naśladować mój styl w kolejnych odpowiedziach.`);
                        setTranscriptionText("");
                      }
                    }}
                    disabled={!transcriptionText.trim()}
                    className="w-full bg-green-600 hover:bg-green-700 text-white"
                  >
                    <Brain className="w-4 h-4 mr-2" />
                    {t("cloneYourself.analyzeButton")}
                  </Button>
                </div>
              )}

              {/* CTA Fields - shown only when needed */}
              {currentModule === "reel-creator" && reelWithCTA && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 p-2 bg-muted/50 border border-border rounded-lg">
                  <Input
                    value={ctaAction}
                    onChange={(e) => setCtaAction(e.target.value)}
                    placeholder={t("reelCreator.ctaActionPlaceholder")}
                    className="bg-background border-border text-foreground placeholder:text-muted-foreground text-xs h-8"
                  />
                  <Input
                    value={ctaReason}
                    onChange={(e) => setCtaReason(e.target.value)}
                    placeholder={t("reelCreator.ctaReasonPlaceholder")}
                    className="bg-background border-border text-foreground placeholder:text-muted-foreground text-xs h-8"
                  />
                </div>
              )}

              {/* YouTube Mode Workflow Controls */}
              {currentModule === "youtube-mode" && (
                <div className="p-3 bg-red-50 dark:bg-transparent dark:bg-gradient-to-r dark:from-red-900/30 dark:to-red-800/30 border border-red-200 dark:border-red-700/50 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <Youtube className="w-5 h-5 text-red-500 dark:text-red-400" />
                      <span className="text-sm font-medium text-red-900 dark:text-white">
                        {t("youtubeMode.title")}
                      </span>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setYoutubeStep("research")}
                        className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                          youtubeStep === "research"
                            ? "bg-red-500 text-white"
                            : "bg-red-100 text-red-400 hover:bg-red-200 dark:bg-white/10 dark:text-white/70 dark:hover:bg-white/[0.12]"
                        }`}
                        title={t("youtubeMode.steps.research.label")}
                      ></button>
                      <button
                        onClick={() => setYoutubeStep("outline")}
                        className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                          youtubeStep === "outline"
                            ? "bg-red-500 text-white"
                            : "bg-red-100 text-red-400 hover:bg-red-200 dark:bg-white/10 dark:text-white/70 dark:hover:bg-white/[0.12]"
                        }`}
                        title={t("youtubeMode.steps.outline.label")}
                      ></button>
                      <button
                        onClick={() => setYoutubeStep("script")}
                        className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                          youtubeStep === "script"
                            ? "bg-red-500 text-white"
                            : "bg-red-100 text-red-400 hover:bg-red-200 dark:bg-white/10 dark:text-white/70 dark:hover:bg-white/[0.12]"
                        }`}
                        title={t("youtubeMode.steps.script.label")}
                      ></button>
                    </div>
                  </div>

                  {youtubeStep === "research" && (
                    <div className="text-xs text-red-700 dark:text-white/70">
                      <strong>{t("youtubeMode.steps.research.label")}:</strong>{" "}
                      {t("youtubeMode.steps.research.description")}
                    </div>
                  )}

                  {youtubeStep === "outline" && (
                    <div className="space-y-2">
                      <div className="text-xs text-red-700 dark:text-white/70">
                        <strong>{t("youtubeMode.steps.outline.label")}:</strong>{" "}
                        {t("youtubeMode.steps.outline.description")}
                      </div>
                      <Input
                        value={selectedTitle}
                        onChange={(e) => setSelectedTitle(e.target.value)}
                        placeholder={t("youtubeMode.titlePlaceholder")}
                        className="bg-white dark:bg-white/[0.08] border-red-200 dark:border-white/[0.1] text-foreground placeholder:text-muted-foreground text-xs h-8"
                      />
                    </div>
                  )}

                  {youtubeStep === "script" && (
                    <div className="text-xs text-red-700 dark:text-white/70">
                      <strong>{t("youtubeMode.steps.script.label")}:</strong>{" "}
                      {t("youtubeMode.steps.script.description")}
                    </div>
                  )}

                  <div className="flex justify-end mt-3">
                    <Button
                      onClick={async () => {
                        let chatId = currentChatId;
                        if (!chatId) {
                          // Create new conversation if none exists
                          const newConversation = await createConversationMutation.mutateAsync({
                            moduleId: currentModule!,
                            title: t("youtubeMode.filmTitle"),
                          });
                          chatId = newConversation.id;
                        }

                        if (youtubeStep === "research") {
                          await sendMessageMutation.mutateAsync({
                            conversationId: chatId!,
                            content: "Pomóż mi zrobić film na YouTube - znajdź mi popularne tytuły",
                          });
                        } else if (youtubeStep === "outline") {
                          const outlineMsg = selectedTitle.trim()
                            ? `Stwórz szczegółowy outline do filmu YouTube o tytule: "${selectedTitle.trim()}"`
                            : "Stwórz szczegółowy outline do filmu YouTube";
                          await sendMessageMutation.mutateAsync({
                            conversationId: chatId!,
                            content: outlineMsg,
                          });
                        } else if (youtubeStep === "script") {
                          await sendMessageMutation.mutateAsync({
                            conversationId: chatId!,
                            content: "Napisz pełen skrypt filmu YouTube na podstawie tego outline",
                          });
                        }
                      }}
                      disabled={sendMessageMutation.isPending}
                      size="sm"
                      className="text-xs bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
                    >
                      {sendMessageMutation.isPending
                        ? t("youtubeMode.creating")
                        : youtubeStep === "research"
                          ? t("youtubeMode.startResearch")
                          : youtubeStep === "outline"
                            ? t("youtubeMode.createOutline")
                            : t("youtubeMode.writeScript")}
                    </Button>
                  </div>
                </div>
              )}

              {currentModule === "stories-creator" && (
                <>
                  <StoriesControlsBar />
                  {hasMessages && (
                    <button
                      type="button"
                      onClick={() => setStoriesPresetsOpen(true)}
                      className="self-start inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] text-white/60 hover:text-white bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.08] hover:border-white/[0.15] transition-all"
                    >
                      <LayoutGrid className="w-3 h-3" /> Galeria scenariuszy
                    </button>
                  )}
                </>
              )}

              <Dialog open={storiesPresetsOpen} onOpenChange={setStoriesPresetsOpen}>
                <DialogContent className="bg-[#0a0c10] border-white/[0.1] text-white max-w-3xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Galeria scenariuszy Stories</DialogTitle>
                  </DialogHeader>
                  <StoriesPresetGallery
                    onSelectPreset={(prompt) => {
                      setInput(prompt);
                      setStoriesPresetsOpen(false);
                    }}
                  />
                </DialogContent>
              </Dialog>

              {/* Text input and send button container - fits to content (hidden in ad-creator graphic/library/pipeline mode) */}
              {!(
                currentModule === "ad-creator" &&
                (adCreatorMode === "graphic" ||
                  adCreatorMode === "video" ||
                  adCreatorMode === "library" ||
                  adCreatorMode === "pipeline" ||
                  adCreatorMode === "ugc")
              ) && (
                <div className="flex items-end gap-3">
                  <div className="flex-1">
                    <Textarea
                      ref={textareaRef}
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                      onPaste={handlePaste}
                      placeholder={
                        currentModule === "youtube-mode"
                          ? youtubeStep === "research"
                            ? t("inputPlaceholders.youtubeResearch")
                            : youtubeStep === "outline"
                              ? t("inputPlaceholders.youtubeOutline")
                              : t("inputPlaceholders.youtubeScript")
                          : currentModule === "campaign-creator"
                            ? t("inputPlaceholders.campaignCreator")
                            : t("inputPlaceholders.default", {
                                name:
                                  (currentModule ? t(`moduleNames.${currentModule}`) : null) ||
                                  currentModuleData?.name ||
                                  "AI",
                              })
                      }
                      className="w-full p-3 border border-white/[0.08] bg-white/[0.03] text-white placeholder-white/30 rounded-xl resize-none focus:ring-2 focus:ring-[#ef3e29]/40 focus:border-[#ef3e29]/30 transition-all leading-6 overflow-y-auto"
                      disabled={isLoading && !tempUserMessage}
                      style={{
                        lineHeight: "24px",
                        height: "96px", // Initial height for 4 lines
                        minHeight: "96px",
                        maxHeight: "288px",
                      }}
                    />

                    {/* Character counter — visible when >70% of limit */}
                    {(() => {
                      const limit = getContentLimit(currentModule);
                      const len = input.length;
                      const ratio = len / limit;
                      if (ratio < 0.7) return null;
                      const isOver = len > limit;
                      return (
                        <div
                          className={`text-xs mt-1 text-right ${isOver ? "text-red-400 font-semibold" : ratio > 0.9 ? "text-amber-400" : "text-white/40"}`}
                        >
                          {t("messages.charCounter", {
                            len: len.toLocaleString("pl-PL"),
                            limit: limit.toLocaleString("pl-PL"),
                          })}
                          {isOver && ` ${t("messages.charCounterOver")}`}
                        </div>
                      );
                    })()}

                    {selectedImage && (
                      <div className="flex items-center space-x-2 px-2 py-1 bg-green-900/50 text-green-400 rounded text-sm border border-green-700/50 mt-2">
                        <Check className="w-4 h-4" />
                        <span>{t("messages.imageAttached")}</span>
                        <button
                          onClick={() => {
                            setSelectedImage("");
                            setImageDescription("");
                          }}
                          className="ml-1 hover:text-green-300"
                        ></button>
                      </div>
                    )}

                    {selectedFile && (
                      <div className="flex items-center space-x-2 px-2 py-1 bg-blue-900/50 text-blue-400 rounded text-sm border border-blue-700/50 mt-2">
                        <span>{t("messages.fileAttached", { name: selectedFile.name })}</span>
                        <button
                          onClick={() => setSelectedFile(null)}
                          className="ml-1 hover:text-blue-300"
                        ></button>
                      </div>
                    )}
                  </div>

                  <div className="flex space-x-2 items-center">
                    {(currentModule === "offer-builder" ||
                      (currentModule === "ad-creator" && adCreatorMode === "text")) && (
                      <Button
                        onClick={() => fileInputRef.current?.click()}
                        variant="outline"
                        size="sm"
                        className="h-12 px-3 rounded-xl bg-white/[0.08] border-white/[0.1] text-white hover:bg-white/10"
                        title={t("fileUpload.attachFileTitle")}
                      >
                        <Paperclip size={16} />
                      </Button>
                    )}

                    <Button
                      onClick={handleSendMessage}
                      disabled={
                        (!input.trim() && !selectedImage && !selectedFile) ||
                        (isLoading && !tempUserMessage)
                      }
                      className="bg-[#ef3e29] hover:bg-[#ef3e29]/90 text-white px-4 md:px-6 py-3 h-12 rounded-xl font-medium transition-all flex items-center space-x-2 flex-shrink-0 disabled:opacity-50 shadow-lg shadow-[#ef3e29]/20"
                    >
                      <span className="hidden md:inline">{t("messages.sendButton")}</span>
                      <Send size={16} />
                    </Button>
                  </div>
                </div>
              )}

              {/* Hidden file inputs */}
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleImageUpload(file);
                  }
                }}
              />

              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.webp,image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleFileUpload(file);
                  }
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <PaywallModal open={paywallOpen} onClose={() => setPaywallOpen(false)} />
      <DemoBookingModal
        open={demoModalOpen}
        onClose={() => setDemoModalOpen(false)}
        stage={demoModalStage}
      />
      <OfferSaveDialog
        open={offerSaveDialogContent !== null}
        onOpenChange={(open) => {
          if (!open) setOfferSaveDialogContent(null);
        }}
        content={offerSaveDialogContent ?? ""}
        agencyClient={
          isAgencyUser && selectedAdCreatorClient
            ? { id: selectedAdCreatorClient.id, name: selectedAdCreatorClient.name }
            : null
        }
        onManageSheets={() => setOfferSheetModalOpen(true)}
      />
      <Dialog
        open={offerSheetPreview !== null}
        onOpenChange={(open) => {
          if (!open) setOfferSheetPreview(null);
        }}
      >
        <DialogContent className="bg-[#0a0c10] border-white/[0.1] max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-white">{offerSheetPreview?.title || ""}</DialogTitle>
            <DialogDescription className="text-white/50 text-xs">
              {t(
                "offerSheetPanel.previewDescription",
                "Podgląd treści arkusza ofertowego (tylko do odczytu)."
              )}
            </DialogDescription>
          </DialogHeader>
          <pre className="flex-1 overflow-auto text-xs text-white/80 whitespace-pre-wrap font-mono bg-black/30 p-3 rounded-lg border border-white/[0.06]">
            {offerSheetPreview?.content?.trim()
              ? offerSheetPreview.content
              : t("offerSheetPanel.noContent", "(arkusz nie zawiera treści)")}
          </pre>
        </DialogContent>
      </Dialog>
    </>
  );
}
